import type { AltBugConfig, AnexoDoChamado, Anotacao, Chamado, ChamadoLocal, RespostaPublica } from "../types";
import { ConsoleBuffer } from "../capture/console";
import { capturarTela, escolherArquivo, escolherImagem, normalizarImagem, suportaCapturaTela } from "../capture/screenshot";
import { rasterizarPagina } from "../capture/rasterizar";
import { coletarMeta } from "../capture/meta";
import { baloesDasMarcas, pinosDasMarcas, pontoDaMarca, svgDasMarcas } from "../annotate/render";
import { anotacaoValida, normalizarPonto } from "@anotacao";
import { enviarReport } from "../intake/enviar";
import { exibirHotkey } from "./hotkey";
import { linkDoPortal } from "../intake/portal";
import { buscarAnexo, buscarChamado, buscarResumo, enviarResposta } from "../chamados/api";
import { estadoDoLauncher, naoVistas } from "../chamados/badge";
import { avisoDoChamado, chamadoParaAvisar, type AvisoDeResposta } from "../chamados/aviso";
import { separarChamados, type ItemDaLista } from "../chamados/lista";
import { dataCurta, horaCurta } from "../chamados/formato";
import { planejarThread } from "../chamados/thread";
import { criarPoller, type Poller } from "../chamados/poller";
import { contaConfere, entrarPorPopup, type SessaoWidget } from "../chamados/sessao";
import {
  esquecerChamados,
  esquecerSessao,
  gravarOculto,
  gravarReporterEmail,
  gravarSessao,
  lerAvisados,
  lerChamados,
  lerOculto,
  lerReporterEmail,
  lerSessao,
  marcarAvisado,
  marcarVistas,
  registrarChamado,
  registrarDaSessao,
  relerChamados,
  renomearAvisados,
  renomearChamados,
} from "../chamados/storage";
import { emailDoHostUtilizavel, emailParece, htmlDoCampoEmail } from "./campoEmail";
import { bolhasNaTela, costurarNaTela } from "./costura";
import {
  ROTULO_TALO,
  deveRepintarRepouso,
  formaDoLauncher,
  nasceVisivel,
  perguntaAoEsconder,
  type FormaDoLauncher,
} from "./launcher";
import { escapar } from "./escapar";
import { CSS } from "./styles";
import { ICON_CAIXA, ICON_CAMERA, ICON_CHAT_NOVO, ICON_CLIPE, ICON_ENGRENAGEM, ICON_VOLTAR, ICON_X, iconeDe } from "./icons";

// Movimento (px) abaixo do qual um gesto é "clique" (ponto); acima, é "arraste" (caixa).
/** Para onde o login volta quando dá certo: os dois caminhos do menu (DEV-144), cada um
    com a sua primeira tela — a captura ou o formulário direto —, mais o `report`, que é a
    volta para um formulário JÁ preenchido (a sessão venceu na hora de enviar). */
type DestinoDoLogin = "captura" | "chamado" | "report";

const LIMIAR_ARRASTE = 6;

/** Teto do anexo do report (DEV-211) — o MESMO da print no servidor (`max:20480` KB).
    Conferido no cliente só para não fazer a pessoa esperar um upload que será recusado. */
const MAX_ANEXO_BYTES = 20 * 1024 * 1024;

/** Quanto o balão de resposta nova fica na tela (DEV-216). Generoso para as 3 linhas que
    ele mostra — e ele pode sumir sozinho justamente porque não é o único caminho: a
    contagem continua na badge e na linha "Meus chamados" depois que ele vai embora. */
const MS_AVISO = 10_000;

/** Folga entre o balão e o launcher. */
const GAP_AVISO = 10;

/** O "ainda sem respostas" da conversa. Constante porque tem DOIS usos que precisam ser o
    mesmo nó: o render pinta este placeholder, e a costura do poll (DEV-210) o transforma na
    thread quando a primeira resposta chega — e o devolve se o time recolher todas. */
const HTML_THREAD_VAZIA =
  '<p class="ch-vazio" data-thread-vazia>Ainda sem respostas. Você verá aqui quando o time responder.</p>';

/** Controlador da UI do widget (Shadow DOM isolado). Tela ÚNICA: print anotável à
    esquerda, formulário à direita — anotar continua disponível enquanto se escreve.
    Interação por GESTO: clique = ponto, arraste = caixa. Sem paleta de ferramentas. */
export class WidgetUI {
  private readonly host: HTMLDivElement;
  private readonly root: ShadowRoot;
  private readonly container: HTMLDivElement;

  private anotacoes: Anotacao[] = [];
  private preview: Anotacao | null = null;
  /** Tipo escolhido no caminho secundário. `null` = caminho principal (o tipo é o do
      relator, e a chave nem viaja no payload). */
  private tipoEscolhido: string | null = null;
  private screenshot: Blob | null = null;
  private screenshotUrl: string | null = null;
  private enviando = false;
  /** O launcher está na tela? A FORMA em que ele aparece é do aparelho — bolinha no
      desktop, talo no celular (ver `forma()`); esta flag só diz se ele existe. */
  private fabVisivel = true; // com hotkey ligado, nasce oculto (definido no construtor)
  /** O menu está aberto por TOQUE (DEV-224). No desktop quem abre é o `:hover` do CSS e isto
      fica sempre em `false`; no celular não há hover, então o estado precisa ser nosso. */
  private menuAberto = false;
  /** Quantas respostas novas havia quando o launcher foi escondido — a linha de base que
      faz a reexibição por novidade (DEV-176) valer só para o que chegar DEPOIS. Nasce em 0
      a cada load: resposta pendente traz o botão de volta, que é o ponto da feature. */
  private novasAoEsconder = 0;
  /** Chamados abertos neste browser (do localStorage) e o que o servidor sabe deles.
      Ambos vivem na INSTÂNCIA, nunca em módulo: pelo DEV-106 o widget é destruído e
      recriado quando a identidade do host muda, e estado de módulo sobreviveria a isso. */
  private locais: ChamadoLocal[] = [];
  private remotos: Chamado[] = [];
  private poller: Poller | null = null;
  /** O balão de resposta nova na tela (DEV-216), quando há. Vive na INSTÂNCIA, e não só no
      DOM, porque o `renderFab` é refeito a cada mexida na badge — sem o estado, o primeiro
      tick depois do aviso o apagaria. */
  private aviso: AvisoDeResposta | null = null;
  private avisoTimer: ReturnType<typeof setTimeout> | null = null;
  /** Sessão do login por popup (DEV-109), quando há. */
  private sessao: SessaoWidget | null = null;
  /** O que estava escrito quando a sessão venceu no envio (DEV-260) — devolvido ao
      formulário depois do login. `null` fora desse caminho. */
  private rascunhoPendente: { titulo: string; descricao: string; email: string } | null = null;
  /** `portalUrl` do servidor: vem da config remota no init e é reconfirmada pelo 202 do
      report. É ela que diz onde o FRONT está — e sem ela o popup de login iria para a
      origem do INTAKE, onde a rota não existe (era o furo achado no primeiro QA). */
  private portalUrl: string | undefined;
  private entrando = false;
  /** A próxima entrada tem de passar pelo SELETOR de conta (DEV-316)? Fica ligado quando
      uma sessão é recusada por ser de outra conta — sem isso o popup reemite a mesma
      conta que acabou de ser recusada, e o botão "Entrar" vira um laço. */
  private trocarConta = false;
  /** Remove os listeners de foco/visibilidade que disparam a consulta imediata. */
  private limpezaFoco: (() => void) | null = null;
  /** O aparelho é de TOQUE? A consulta é `(hover: none)`, a MESMA que o CSS usa para decidir
      que o menu não abre sozinho — e é de propósito que seja a mesma: são dois lados de uma
      decisão só, e duas condições diferentes fariam o JS pintar um estado que o CSS não
      pinta. Guardada como `MediaQueryList` (e não como boolean) para ser lida a cada uso: o
      valor muda ao ligar/desligar a emulação de toque do DevTools, que é onde o celular é
      conferido. `null` só em ambiente sem `matchMedia`. */
  private readonly mqToque: MediaQueryList | null;
  /** Solta os listeners que fecham o menu aberto por toque. */
  private limpezaMenu: (() => void) | null = null;
  // Posição arrastada (persistida). Ancorada pelo lado do ícone (`lado`+`x` = distância
  // dessa borda) p/ o ícone não sair do lugar; o rótulo expande para o outro lado.
  private fabPos: { lado: "left" | "right"; x: number; top: number } | null = null;

  /** objectURLs dos anexos abertos na conversa (report e respostas), para revogar ao sair. */
  private urlsAnexos: string[] = [];

  /** A seção de encerrados (DEV-183) está expandida? Volta a fechar a cada abertura do
      painel: o padrão é a lista curta, e expandir é uma escolha daquela visita. */
  private encerradosAbertos = false;

  /** A imagem que vai junto da próxima resposta do cliente (DEV-213), já normalizada.
      Uma por mensagem, e limpa só no SUCESSO do envio. */
  private imagemDaResposta: Blob | null = null;

  /** O arquivo que vai junto do report (DEV-211), de qualquer tipo. Fica no campo, e não no
      DOM, porque a tela do formulário é reconstruída ao capturar a print — como o rascunho
      do texto, a escolha tem de sobreviver ao re-render. */
  private anexoDoReport: File | null = null;

  /** A print está sendo GERADA agora? É o que faz o formulário abrir com o painel da
      imagem em espera, no lugar de a tela ficar parada esperando (ou de um toast dizendo
      isso por fora). Quem espera, espera DENTRO do formulário — e escrevendo. */
  private gerandoPrint = false;

  /** Uma rasterização em curso? Ela leva SEGUNDOS (o clone da página é O(nós)), e sem esta
      trava o segundo toque de quem achou que o primeiro não pegou dispara outra em paralelo
      — duas varreduras do DOM disputando a mesma CPU que já estava devagar. */
  private rasterizando = false;

  /** Alguma tentativa de print falhou NESTE report (DEV-277)? É o que faz o formulário
      revelar o caminho ALTERNATIVO — a rasterização no desktop, o print do aparelho no
      celular. Progressivo de propósito: quem acerta de primeira nunca vê dois botões
      perguntando a mesma coisa, e quem falhou não fica sem saída. Zera a cada report. */
  private printFalhou = false;

  constructor(
    private readonly cfg: AltBugConfig,
    private readonly consoleBuffer: ConsoleBuffer,
  ) {
    this.host = document.createElement("div");
    this.host.setAttribute("data-altbug-host", "");
    this.host.style.cssText = "position:fixed;inset:0;z-index:2147483000;pointer-events:none;";
    this.root = this.host.attachShadow({ mode: "closed" });
    const style = document.createElement("style");
    style.textContent = CSS;
    this.container = document.createElement("div");
    this.container.className = "altbug";
    this.container.dataset.tema = this.resolverTema();
    // Cor de destaque configurável: sobrepõe os tokens de acento (botão + acentos).
    this.container.style.setProperty("--ab-accent", cfg.cor);
    this.container.style.setProperty("--ab-accent-hover", escurecer(cfg.cor, 0.14));
    this.root.append(style, this.container);
    this.conterTeclado();
    document.body.appendChild(this.host);
    this.mqToque = consultarToque();
    // Oculto por config (hotkey) ou pelo "manter escondido" — que é só do desktop, onde o
    // `?` desfaz a escolha (DEV-224).
    this.fabVisivel = nasceVisivel(cfg.hotkey, lerOculto(cfg.token), this.ehToque());
    this.fabPos = this.carregarPosFab();
    this.portalUrl = cfg.portalUrl;
    this.locais = lerChamados(cfg.token);
    this.sessao = lerSessao(cfg.token);
    // Sessão guardada por OUTRA pessoa neste browser (DEV-316). Some sem alarde: o host
    // diz que quem está na página agora é outra, e um widget que age em nome do anterior é
    // exatamente o que o DEV-106 corrigiu do outro lado. Quem quiser a lista entra de novo.
    if (this.sessao !== null && !contaConfere(this.emailDoHost(), this.sessao.email)) {
      this.sessao = null;
      this.locais = esquecerSessao(cfg.token);
    }
    this.renderRepouso();
    this.mqToque?.addEventListener("change", this.onTrocaDeAparelho);
    this.ligarPoller();
  }

  /**
   * O aparelho trocou de categoria — na prática, alguém ligou a emulação de toque no
   * DevTools (ou plugou um mouse num tablet).
   *
   * Repintar é obrigatório e não é enfeite de QA: bolinha e talo são o MESMO nó com classes
   * diferentes, então sem isto a tela fica com a forma do aparelho anterior até o próximo
   * render — e quem confere o celular no navegador de desktop veria a bolinha e concluiria
   * que o card não foi entregue. O menu aberto por toque morre junto: no desktop quem manda
   * nele é o `:hover`, e uma classe pendurada deixaria o painel aberto sem cursor em cima.
   */
  private readonly onTrocaDeAparelho = (): void => {
    this.menuAberto = false;
    if (this.emRepouso()) this.renderRepouso();
  };

  /** O aparelho é de toque? Lido a cada uso — ver `mqToque`. */
  private ehToque(): boolean {
    return this.mqToque?.matches ?? false;
  }

  /**
   * Liga o acompanhamento em segundo plano: é ele que acende a badge de resposta nova sem
   * a pessoa abrir o painel. Só existe quando há chamado guardado neste browser — sem
   * chamado não há nada para consultar, e o widget de quem nunca reportou não faz uma
   * requisição a mais do que fazia antes.
   *
   * Morre no `destruir()`. Isso não é detalhe: o widget vive fora do React (em
   * `document.body`) e é destruído/recriado quando a identidade do host muda — um poller
   * órfão seguiria batendo na API com os tokens da pessoa anterior.
   */
  private ligarPoller(): void {
    // Idempotente: além do construtor, é chamado quando o PRIMEIRO chamado é guardado
    // (quem acabou de reportar não tinha nada para acompanhar quando o widget montou).
    if (this.poller !== null || (this.locais.length === 0 && this.sessao === null)) return;
    this.poller = criarPoller({
      // 15s, o mesmo ritmo do delta do app. Com 60s a resposta do time podia levar um
      // minuto para acender a badge, e na prática quem percebia era quem abria o painel
      // por conta própria — que é exatamente o que a badge existe para dispensar.
      // Cabe no teto: 4 req/min por aba contra 60/min por IP no bucket de leitura.
      intervaloMs: 15_000,
      visivel: () => document.visibilityState === "visible",
      executar: () => this.sincronizarEmFundo(),
    });
    this.poller.iniciar();
    this.ligarFoco();
  }

  /**
   * Consulta na hora quando a janela volta ao foco (ou a aba reaparece).
   *
   * Sem isto, quem deixa a aba de lado e volta espera até o próximo tick para ver a badge
   * — e é justamente aí que a resposta do time costuma ter chegado. O poller pula o
   * trabalho enquanto a aba está escondida (de propósito: não gasta rede nem orçamento de
   * rate limit), então o retorno é o momento em que a informação está mais atrasada.
   */
  private ligarFoco(): void {
    if (this.limpezaFoco !== null) return;
    const aoVoltar = (): void => {
      if (document.visibilityState !== "visible") return;
      void this.sincronizarEmFundo();
    };
    window.addEventListener("focus", aoVoltar);
    document.addEventListener("visibilitychange", aoVoltar);
    this.limpezaFoco = () => {
      window.removeEventListener("focus", aoVoltar);
      document.removeEventListener("visibilitychange", aoVoltar);
    };
  }

  private pararFoco(): void {
    this.limpezaFoco?.();
    this.limpezaFoco = null;
  }

  /**
   * Consulta o resumo e repinta SÓ a badge — nunca a tela. Se a pessoa está com o painel
   * (ou o fluxo de report) aberto, repintar arrancaria a interface debaixo dela; e a
   * `renderRepouso` só é chamada quando o container está de fato em repouso.
   */
  private async sincronizarEmFundo(): Promise<void> {
    // Conversa aberta na tela: o detalhe DELA é o que interessa, e o resumo alimentaria uma
    // badge que está atrás do painel. Fica em uma requisição por tick, como antes — e o
    // detalhe já traz o total para marcar como visto. É o mesmo desenho do app, onde o
    // delta cai para 30s porque o card aberto se atualiza pelo /completo.
    if (this.codigoNaTela() !== null) {
      await this.atualizarThreadAberta();

      return;
    }

    // `antes` sai da cópia em memória, que é o que está PINTADO — o `consultarResumo` relê
    // o disco depois dele. É essa diferença que repinta a badge que outra aba já zerou.
    const antes = naoVistas(this.locais, this.remotos);
    const formaAntes = this.forma();
    const r = await this.consultarResumo();
    if (r.falhou) return; // silencioso: é trabalho de fundo, não pedido da pessoa

    const depois = naoVistas(this.locais, this.remotos);
    // Resposta nova traz o launcher de volta (DEV-176): escondido, a badge não tinha onde
    // aparecer e o aviso virava silêncio. O `renderRepouso` abaixo é que o pinta — e se um
    // painel estiver aberto ele não roda, mas a flag já ficou de pé: ao fechar, o botão
    // está lá. A escolha de esconder não é apagada do storage; ela só perde para a novidade.
    const launcher = estadoDoLauncher(this.fabVisivel, depois, this.novasAoEsconder);
    this.fabVisivel = launcher.visivel;
    this.novasAoEsconder = launcher.base;

    // Repinta quando o launcher acabou de voltar — esse caso existe sozinho, e sem ele o
    // botão só apareceria no próximo tick que mexesse na contagem (que pode não vir) — ou
    // quando a contagem mudou E há badge para mostrá-la. O segundo qualificador é do
    // DEV-222: com o talo, o ramo escondido virou um nó visível e focável, e repintá-lo por
    // uma contagem que ele não mostra tiraria o foco de quem chegou nele pelo teclado.
    if (deveRepintarRepouso(formaAntes, this.forma(), antes, depois) && this.emRepouso()) {
      this.renderRepouso();
    }
    // Depois do repaint: o balão é pintado por cima do launcher que acabou de existir.
    await this.talvezAvisar();
  }

  /**
   * O balão de resposta nova (DEV-216): a badge diz que HÁ novidade, ele diz QUAL é.
   *
   * O texto não vem do resumo — ele só traz contagem —, então há uma requisição a mais
   * aqui: o DETALHE do chamado escolhido. Ela acontece **só quando há o que anunciar**, o
   * que na prática é uma vez por resposta do time, e não a cada 15s; é o mesmo
   * `buscarChamado` que o tick já usa quando a conversa está aberta. A alternativa era
   * engordar o resumo com um trecho de texto em TODO poll de TODO widget, para usá-lo
   * quase nunca.
   *
   * **Ler aqui não conta como ver.** `marcarVistas` não é chamado de propósito: a badge
   * fica acesa até a pessoa abrir a conversa de verdade. Quem carimba é o
   * `marcarAvisado`, e o que ele evita é repetir o MESMO aviso — não a badge.
   */
  private async talvezAvisar(): Promise<void> {
    // Um balão por vez; e com o painel aberto (ou o launcher escondido) não há onde ele
    // apareça — a pessoa já está no widget, ou pediu para não ver o widget.
    if (this.aviso !== null || !this.fabVisivel || !this.emRepouso()) return;

    const alvo = chamadoParaAvisar(this.locais, this.remotos, lerAvisados(this.cfg.token));
    if (alvo === null) return;
    const local = this.locais.find((c) => c.codigo === alvo.codigo);
    if (!local) return;

    const chamado = await buscarChamado(this.cfg.endpoint, this.cfg.token, alvo.codigo, local.token, this.sessao?.sessao);
    if (!chamado) return; // silencioso: é trabalho de fundo, não pedido da pessoa
    const aviso = avisoDoChamado(chamado);
    if (aviso === null) return;

    // Carimba ANTES de pintar: se a pessoa abriu um painel durante a requisição, o aviso
    // se perde — e insistir nele no tick seguinte seria justamente o comportamento
    // repetitivo que o carimbo existe para evitar.
    marcarAvisado(this.cfg.token, aviso.codigo, aviso.respostas);
    if (this.aviso !== null || !this.fabVisivel || !this.emRepouso()) return;
    this.mostrarAviso(aviso);
  }

  /** O container está mostrando o FAB (nenhum painel/overlay aberto)? */
  private emRepouso(): boolean {
    return this.container.querySelector(".overlay") === null;
  }

  /** Em que forma o launcher está agora (DEV-224) — a regra pura vive em `./launcher`. */
  private forma(): FormaDoLauncher {
    return formaDoLauncher(this.fabVisivel, this.ehToque(), this.menuAberto);
  }

  /** Encolhido no talo? É o que muda o significado do clique, do Enter e do arraste. */
  private emTalo(): boolean {
    return this.forma() === "talo";
  }

  /**
   * O toque no talo: abre o mesmo menu que o hover abre no desktop.
   *
   * Enquanto ele está aberto, um toque em QUALQUER outro lugar o fecha — é o que se espera
   * de um menu no dedo, e sem isso ele ficaria pendurado sobre o site anfitrião até a pessoa
   * escolher alguma coisa. O toque chega aqui como o `host` (shadow fechado retarget tudo o
   * que nasce dentro dele), então distinguir dentro de fora é uma comparação de nó.
   *
   * `focar` só é pedido por quem chegou aqui pelo TECLADO (celular com teclado plugado, ou
   * a emulação de toque no desktop). O repaint destrói o nó que estava focado, e sem devolver
   * o foco a pessoa cai no `<body>`: o launcher é o ÚLTIMO da ordem de tabulação (o host é o
   * último filho do `<body>`), então recuperá-lo custaria atravessar a página inteira. Pelo
   * dedo isso não vale — ali roubar o foco é que seria surpresa.
   */
  private abrirMenu(focar = false): void {
    if (this.menuAberto) return;
    this.menuAberto = true;
    this.renderRepouso();
    if (focar) this.container.querySelector<HTMLElement>(".fab")?.focus();

    const fora = (e: Event): void => {
      if (e.target === this.host) return;
      this.fecharMenu();
    };
    // Captura: um host que engole `pointerdown` na borbulha (carrossel, editor) deixaria o
    // menu aberto para sempre. `Escape` fecha pelo mesmo motivo do `?`: teclado plugado.
    document.addEventListener("pointerdown", fora, true);
    const tecla = (e: KeyboardEvent): void => {
      if (e.key === "Escape") this.fecharMenu();
    };
    window.addEventListener("keydown", tecla, true);
    this.limpezaMenu = () => {
      document.removeEventListener("pointerdown", fora, true);
      window.removeEventListener("keydown", tecla, true);
    };
  }

  /**
   * Volta ao talo. Idempotente, e no desktop é um no-op inteiro — é o que deixa os itens do
   * menu chamarem isto sem perguntar em que aparelho estão.
   *
   * O repaint é condicionado ao repouso porque este é também o caminho de saída dos itens:
   * quem abriu uma tela já pintou por cima, e repintar ali arrancaria o painel recém-aberto.
   */
  private fecharMenu(): void {
    if (!this.menuAberto) return;
    this.menuAberto = false;
    this.soltarMenu();
    if (this.emRepouso()) this.renderRepouso();
  }

  /** Só os listeners de "fechar por fora" — sem tocar no estado nem na tela. */
  private soltarMenu(): void {
    this.limpezaMenu?.();
    this.limpezaMenu = null;
  }

  /** O código do chamado cuja CONVERSA está na tela, ou `null`. Lido do DOM, como o
      `emRepouso()`: um campo espelhando "que tela está aberta" se perde na primeira tela
      nova que alguém escrever — e aqui o preço de errar é atualizar a tela errada. */
  private codigoNaTela(): string | null {
    return this.container.querySelector<HTMLElement>('.painel[data-tela="chamado"]')?.dataset.codigo ?? null;
  }

  /**
   * Acerta o código guardado no DOM da conversa ABERTA quando ele foi reemitido no meio
   * da sessão (DEV-302).
   *
   * Sem isto o desalinhamento não dá erro — dá MUDEZ: `atualizarThreadAberta` e
   * `responder` fazem `locais.find(c => c.codigo === codigoNaTela())` e desistem em
   * silêncio quando não acham. A thread para de atualizar e o botão de enviar deixa de
   * fazer qualquer coisa, com o rascunho intacto na tela e nada no console.
   */
  private rechavearTelaAberta(pares: Array<{ de: string; para: string }>): void {
    const painel = this.container.querySelector<HTMLElement>('.painel[data-tela="chamado"]');
    if (!painel) return;
    const par = pares.find((p) => p.de === painel.dataset.codigo);
    if (par) painel.dataset.codigo = par.para;
  }

  /**
   * Traz a conversa ABERTA para o estado do servidor sem repintar a tela (DEV-210).
   *
   * Antes o tick só mexia na badge, e com o chamado aberto a resposta do time não aparecia
   * até fechar e reabrir — a badge acesa por trás de um painel que mostrava a conversa
   * velha. Repintar era o que não se podia fazer: arrancaria o rascunho, o foco e a rolagem
   * de quem está escrevendo. Daí costurar.
   */
  private async atualizarThreadAberta(): Promise<void> {
    const codigo = this.codigoNaTela();
    if (codigo === null) return;
    const local = this.locais.find((c) => c.codigo === codigo);
    if (!local) return;

    const chamado = await buscarChamado(this.cfg.endpoint, this.cfg.token, codigo, local.token, this.sessao?.sessao);
    if (!chamado) return; // silencioso: é trabalho de fundo, não pedido da pessoa
    if (this.codigoNaTela() !== codigo) return; // navegou/fechou durante a requisição

    // Ler CONTA COMO VER, como no `abrirChamado`: a mensagem está na tela da pessoa, então
    // a badge não pode acender por ela. O total vem do servidor (excluir DIMINUI).
    this.locais = marcarVistas(this.cfg.token, codigo, chamado.respostas);
    this.remotos = [chamado, ...this.remotos.filter((r) => r.codigo !== codigo)];
    this.costurarThread(codigo, chamado);
  }

  /**
   * Aplica na tela o plano do `planejarThread` — insere, remove e repinta bolha por bolha.
   *
   * O que ESTA função protege, e é o motivo de ela não ser um `innerHTML =`: o texto que a
   * pessoa está digitando, o foco no campo e a rolagem. Seguir a conversa só acontece para
   * quem já estava no fim; quem subiu para reler não é arrastado pelo tick.
   */
  private costurarThread(codigo: string, c: Chamado): void {
    const corpo = this.container.querySelector<HTMLDivElement>(".painel-corpo");
    if (!corpo) return;

    const plano = planejarThread(bolhasNaTela(corpo), c.respostasThread ?? []);
    if (plano.anexar.length === 0 && plano.remover.length === 0 && plano.trocar.length === 0) {
      this.atualizarSelo(c);

      return;
    }

    // "Estava no fim" tem de ser medido ANTES de inserir: depois o scrollHeight já cresceu.
    const noFim = corpo.scrollHeight - corpo.scrollTop - corpo.clientHeight < 24;

    const mexidas = costurarNaTela(corpo, plano, (r) => this.htmlDaMensagem(r), HTML_THREAD_VAZIA);
    // Os binários das mensagens que entraram ou foram repintadas: mesmo caminho do render,
    // mesma vida de blob (soltos na troca de tela pelo `liberarAnexos`).
    for (const r of mexidas) {
      for (const a of r.anexos ?? []) void this.carregarAnexo(codigo, a);
    }

    this.atualizarSelo(c);
    if (noFim) corpo.scrollTop = corpo.scrollHeight;
  }

  /** A situação pode ter mudado com a conversa aberta (o time resolveu o chamado). Só o
      selo: o composer não é tocado aqui, para não tirar o campo debaixo de quem digita. */
  private atualizarSelo(c: Chamado): void {
    const selo = this.container.querySelector<HTMLSpanElement>(".ch-selo");
    if (!selo || selo.textContent === c.situacaoLabel) return;
    selo.textContent = c.situacaoLabel;
    selo.classList.toggle("ok", c.situacao === "resolvido");
  }

  /**
   * Consulta o resumo e reconcilia o estado local — o único lugar que fala com
   * `buscarResumo`, para as duas chamadas (painel e poller) tratarem a sessão igual.
   *
   * Quatro reconciliações, e a ORDEM é regra, não estilo:
   *  1. sessão recusada pelo servidor → esquece a sessão E os chamados que só existiam
   *     por identidade (sem token local, ninguém consegue abri-los);
   *  2. chamados que MUDARAM de código → o storage é re-chaveado (DEV-302);
   *  3. chamados novos vindos por identidade → entram no storage (é o que dá badge a eles);
   *  4. códigos que o servidor não reconheceu → saem (chamado apagado, token revogado).
   *
   * O 2 vem antes do 4 porque é o que impede o renome de virar esquecimento: sem ele o
   * código antigo não casa com resposta nenhuma, cai em `desconhecidos` e é apagado com o
   * TOKEN dentro — o cliente perde o acesso ao próprio chamado, sem erro na tela. E vem
   * antes do 3 para a sessão não gravar uma segunda linha do mesmo chamado (com
   * `token: ""`) que o renome teria de deduplicar em seguida.
   */
  private async consultarResumo(): Promise<{ falhou: boolean }> {
    // O disco é dividido entre as abas; a cópia em memória, não (DEV-358). O que outra aba
    // leu, abriu ou esqueceu entra aqui, ANTES do pedido: é desta lista que saem os tokens.
    this.locais = relerChamados(this.cfg.token) ?? this.locais;
    const r = await buscarResumo(
      this.cfg.endpoint,
      this.cfg.token,
      this.locais.map((c) => ({ codigo: c.codigo, token: c.token })),
      this.sessao?.sessao,
    );
    if (r.falhou) return { falhou: true };

    if (r.sessaoExpirada) {
      this.sessao = null;
      this.locais = esquecerSessao(this.cfg.token);
    }
    this.remotos = r.chamados;
    if (r.renomeados.length > 0) {
      this.locais = renomearChamados(this.cfg.token, r.renomeados);
      // O SEGUNDO storage indexado por código. Deixar a chave velha órfã não perde nada
      // — faz o balão reanunciar uma resposta já lida, que é pior que perder.
      renomearAvisados(this.cfg.token, r.renomeados);
      this.rechavearTelaAberta(r.renomeados);
    }
    if (this.sessao !== null) {
      this.locais = registrarDaSessao(this.cfg.token, r.chamados);
    }
    if (r.desconhecidos.length > 0) {
      this.locais = esquecerChamados(this.cfg.token, r.desconhecidos);
    }

    return { falhou: false };
  }

  /**
   * Abre o popup de login e, com a sessão em mãos, recarrega a lista.
   *
   * O `window.open` acontece dentro do handler do clique (sem `await` antes): fora do
   * gesto o bloqueador de popup do browser recusa a janela.
   */
  private entrar(): void {
    if (this.entrando) return;
    this.entrando = true;
    const botao = this.container.querySelector<HTMLButtonElement>('[data-act="entrar"]');
    if (botao) {
      botao.disabled = true;
      botao.textContent = "Entrando…";
    }

    void entrarPorPopup(this.cfg.endpoint, this.portalUrl, this.cfg.token, this.trocarConta)
      .then(async (r) => {
        this.entrando = false;
        if (!this.container.querySelector(".painel")) return; // fechou durante o login
        if (!r.ok) {
          this.renderPainel(); // desfaz o "Entrando…"
          // Só avisa quando a pessoa NÃO viu tela nenhuma. Quem fechou o popup decidiu
          // fechar — e se o servidor recusou (403 "Não há chamados desta conta neste
          // site."), a mensagem estava lá dentro: repeti-la aqui é eco, não informação.
          if (r.motivo === "bloqueado") {
            this.avisoNaLista("Seu navegador bloqueou a janela de login. Libere popups deste site e tente de novo.");
          } else if (r.motivo === "indisponivel") {
            this.avisoNaLista("O login não está disponível neste site. Avise quem cuida do suporte.");
          }
          void this.atualizarChamados();
          return;
        }
        // Conta trocada (DEV-316) — mesma recusa da porta do report, na linguagem da lista.
        const recusa = this.recusarSeOutraConta(r.sessao);
        if (recusa !== null) {
          this.renderPainel();
          this.avisoNaLista(recusa);
          void this.atualizarChamados();

          return;
        }
        this.aplicarSessao(r.sessao);
        this.renderPainel();
        await this.atualizarChamados();
      })
      .catch(() => {
        this.entrando = false;
      });
  }

  /**
   * Abre a configuração DESTE relator no Tasks (DEV-223), em aba nova.
   *
   * Aba nova, e não a mesma: o widget vive num site que não é nosso, e trocar a página
   * de quem só queria mexer num rótulo perderia o que a pessoa estivesse fazendo lá.
   *
   * A URL vem da sessão, já peneirada por `urlDeConfig` na entrada e a cada leitura do
   * storage — a checagem aqui é a do "sumiu no meio do caminho" (saiu, sessão venceu),
   * não a de formato.
   */
  private abrirConfig(): void {
    const url = this.sessao?.configUrl;
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  /** Sai: esquece a sessão e os chamados que dependiam dela. */
  private sair(): void {
    this.sessao = null;
    this.locais = esquecerSessao(this.cfg.token);
    this.remotos = this.remotos.filter((r) => this.locais.some((l) => l.codigo === r.codigo));
    this.renderPainel();
    void this.atualizarChamados();
  }

  /** Tema efetivo: "auto" segue o `prefers-color-scheme`; explícito vence. */
  private resolverTema(): "light" | "dark" {
    if (this.cfg.tema === "light" || this.cfg.tema === "dark") return this.cfg.tema;
    try {
      return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    } catch {
      return "light";
    }
  }

  /** Contém o teclado dentro do widget: eventos de tecla nascidos no shadow não
      devem escapar e disparar atalhos globais da PÁGINA anfitriã (bug DEV-13). Como
      isto vive no próprio widget, protege QUALQUER site que embuta o AltBug — inclusive
      apps de terceiros, onde não dá para mexer no código deles.

      Escutamos na borda do shadow (o próprio `root`) em fase de BORBULHA: quando o
      evento chega aqui, o `<input>`/`<textarea>` interno já o recebeu (a digitação
      segue normal), e o `stopPropagation` o barra ANTES de alcançar os listeners de
      `window`/`document` do host.

      Limite intrínseco: só cobre listeners do host em fase de BORBULHA (o padrão da
      esmagadora maioria — Mousetrap, hotkeys-js, tinykeys, etc.). Um listener do host
      em fase de CAPTURA roda ANTES de o evento entrar no shadow, então nenhum widget
      consegue suprimi-lo sem também bloquear a própria digitação — esse caso precisa
      ceder do lado do host. */
  private conterTeclado(): void {
    const conter = (e: Event): void => e.stopPropagation();
    for (const tipo of ["keydown", "keyup", "keypress"] as const) {
      this.root.addEventListener(tipo, conter);
    }
  }

  /** O que a hotkey aciona. O LISTENER mora em `ui/hotkey.ts`, ligado no escopo
      de módulo do `index.ts` — aqui ele nascia no CONSTRUTOR, ou seja só depois
      do `await` da config remota: a prioridade do Relator contra o host dependia
      de um round-trip de rede terminar. */
  readonly alternarLauncher = (): void => {
    if (this.fabVisivel) this.esconderLauncher();
    else this.mostrarLauncher();
  };

  /** Um fluxo do widget aberto → a tecla não interfere. */
  readonly temOverlayAberto = (): boolean => this.container.querySelector(".overlay") != null;

  /**
   * Traz o launcher de volta — pelo atalho `?`, que é o caminho de volta do desktop.
   *
   * Trazer de volta APAGA o "manter escondido": sem isto o launcher reapareceria agora e
   * sumiria no próximo load, fazendo o caminho de volta parecer quebrado.
   */
  private mostrarLauncher(): void {
    this.fabVisivel = true;
    gravarOculto(this.cfg.token, false);
    this.renderRepouso();
  }

  /**
   * "Esconder", do menu: no desktop abre o diálogo do "reaparecer ao recarregar?", no
   * celular esconde na hora (DEV-224).
   *
   * A pergunta não faz sentido no dedo: a resposta "não" só teria como ser desfeita pela
   * tecla `?`, que não existe ali — era esse beco que o talo do DEV-222 tentava tapar
   * transformando "esconder" em "encolher", e que agora se resolve na origem. Escondido no
   * celular vale para a sessão; o próximo load traz o talo de volta.
   */
  private esconder(): void {
    if (perguntaAoEsconder(this.ehToque())) {
      this.renderEsconder();

      return;
    }
    this.esconderLauncher();
  }

  /**
   * Tira o launcher da tela, venha o pedido do menu ou da tecla `?`.
   *
   * Carimbar a contagem atual é o que impede o poller de desfazer o gesto no tick seguinte
   * (DEV-176): quem esconde com resposta pendente escondeu SABENDO dela, e o botão só volta
   * pelo que chegar depois. Sem isto, esconder com novidade na tela seria impossível.
   */
  private esconderLauncher(): void {
    this.novasAoEsconder = naoVistas(this.locais, this.remotos);
    this.fabVisivel = false;
    this.menuAberto = false;
    this.soltarMenu();
    // O balão mora colado no launcher: sem ele na tela, o aviso ficaria apontando para o
    // nada — e quem acabou de esconder o widget não pediu para continuar vendo um pedaço.
    this.limparAviso();
    this.renderRepouso();
  }

  /* ---------------- API pública ---------------- */

  /** Vai direto para a captura. É o que o host chama no botão "Reportar" dele —
      manter esse atalho é o que permite ao FAB abrir o menu sem tirar nada de ninguém. */
  abrir(): void {
    this.limparAviso(); // o fluxo toma a tela: o balão sai com ela, sem repintar o repouso
    // O menu sai junto, e sem repintar: quem repinta o launcher é quem volta ao repouso
    // (`fechar`), e sem isto ele voltaria com o menu ainda aberto por cima do site.
    this.menuAberto = false;
    this.soltarMenu();
    // Relator que exige login (DEV-260) pede a conta ANTES da captura. É o mesmo princípio
    // do e-mail obrigatório do DEV-103, levado ao caminho inteiro: ninguém deve tirar a
    // print, anotar a tela e escrever o relato para descobrir a regra num 401 no fim.
    if (this.precisaEntrar()) {
      this.renderEntrar("captura");

      return;
    }
    void this.iniciarCaptura();
  }

  /** Abre o painel de suporte (menu + Meus chamados). É o que o FAB faz. */
  abrirPainel(): void {
    this.limparAviso();
    this.encerradosAbertos = false; // cada visita começa pela lista curta
    this.renderPainel();
    void this.atualizarChamados();
  }

  /**
   * O caminho secundário (DEV-144) atrás do mesmo gate do principal (DEV-260): a exigência
   * é do RELATOR, não de um caminho — deixar um deles livre esvaziaria a configuração.
   */
  private abrirCaminhoSecundario(): void {
    if (this.precisaEntrar()) {
      this.renderEntrar("chamado");

      return;
    }
    this.abrirReportSemPrint();
  }

  /** O relator exige login e ainda não há sessão neste browser? */
  private precisaEntrar(): boolean {
    return this.cfg.exigeLogin && this.sessao === null;
  }

  /** A pessoa está identificada por LOGIN neste report? É o que tira o campo de e-mail do
      formulário: com sessão, quem carimba a identidade no chamado é o servidor. */
  private identificadoPorLogin(): boolean {
    return this.cfg.exigeLogin && this.sessao !== null;
  }

  /**
   * A porta do report quando o relator exige login. Tela própria, e não o painel de
   * chamados (que também tem "Entrar para ver todos"): ali o login serve para VER uma
   * lista, aqui ele é a primeira etapa de abrir um chamado — e quem clicou em "Reportar"
   * precisa terminar onde clicou, não numa lista.
   */
  private renderEntrar(destino: DestinoDoLogin, erro?: string): void {
    this.container.innerHTML = `
      <div class="overlay">
        <div class="review entrar">
          <h2>Entre para abrir um chamado</h2>
          <p class="okhint">
            Este site pede que você entre com sua conta — assim o time sabe com quem está
            falando e você acompanha a resposta de qualquer aparelho.
          </p>
          <p class="erro-form"${erro ? "" : " hidden"}>${erro ? escapar(erro) : ""}</p>
          <div class="actions">
            <button class="btn" type="button" data-act="cancel">Cancelar</button>
            <button class="btn primary" type="button" data-act="entrar">${
              this.trocarConta ? "Entrar com outra conta" : "Entrar"
            }</button>
          </div>
        </div>
      </div>`;

    this.container.querySelector<HTMLButtonElement>('[data-act="cancel"]')!
      .addEventListener("click", () => this.fechar());
    // Sem `await` antes do clique: o `window.open` tem de sair no MESMO tick do gesto,
    // senão o bloqueador de popup do browser recusa a janela.
    this.container.querySelector<HTMLButtonElement>('[data-act="entrar"]')!
      .addEventListener("click", () => this.entrarParaReportar(destino));
  }

  /**
   * Entra pelo popup e SEGUE para o caminho que a pessoa pediu — a diferença para o
   * `entrar()` do painel, que volta para a lista.
   *
   * Os desfechos sem sessão ficam na própria tela, com o botão de volta: `cancelado`
   * inclui quem viu a recusa do servidor lá dentro, e repetir a mensagem dele aqui seria
   * eco; o que a tela precisa dizer é que sem entrar não há chamado.
   */
  private entrarParaReportar(destino: DestinoDoLogin): void {
    if (this.entrando) return;
    this.entrando = true;
    const botao = this.container.querySelector<HTMLButtonElement>('[data-act="entrar"]');
    if (botao) {
      botao.disabled = true;
      botao.textContent = "Entrando…";
    }

    void entrarPorPopup(this.cfg.endpoint, this.portalUrl, this.cfg.token, this.trocarConta)
      .then((r) => {
        this.entrando = false;
        // Fechou a tela durante o login (Cancelar, ou `AltBug.fechar()` do host): não
        // sequestra a tela de volta — mas a sessão, se veio, fica guardada.
        const naTela = this.container.querySelector(".review.entrar") !== null;
        if (!r.ok) {
          if (!naTela) return;
          this.renderEntrar(
            destino,
            r.motivo === "bloqueado"
              ? "Seu navegador bloqueou a janela de login. Libere popups deste site e tente de novo."
              : r.motivo === "indisponivel"
                ? "O login não está disponível neste site. Avise quem cuida do suporte."
                : "Para abrir um chamado é preciso entrar.",
          );

          return;
        }
        // Conta trocada (DEV-316): a sessão não é guardada, e a tela volta com o motivo.
        const recusa = this.recusarSeOutraConta(r.sessao);
        if (recusa !== null) {
          if (naTela) this.renderEntrar(destino, recusa);

          return;
        }
        this.aplicarSessao(r.sessao);
        if (!naTela) return;
        if (destino === "captura") void this.iniciarCaptura();
        else if (destino === "chamado") this.abrirReportSemPrint();
        else {
          // Volta ao formulário COM o que estava escrito: quem perdeu a sessão no clique de
          // enviar não pode perder o relato junto — e a print continua na memória, porque
          // este caminho nunca passou por `fechar()`.
          this.renderReport(this.rascunhoPendente ?? undefined);
          this.rascunhoPendente = null;
        }
      })
      .catch(() => {
        this.entrando = false;
      });
  }

  /**
   * A conta que entrou NÃO é a que o host declarou (DEV-316)? Então a sessão é recusada e
   * volta a frase a mostrar; `null` quando confere — ou quando não há o que conferir.
   *
   * Recusar, e não apenas avisar: com `exigeLogin` o e-mail do chamado é o da SESSÃO, então
   * seguir daqui abriria o chamado em nome de quem não está na página — e a pessoa só
   * descobriria quando a resposta não chegasse. Liga junto o `trocarConta`, que é o que faz
   * a próxima tentativa alcançar o seletor de conta em vez de reemitir a mesma.
   */
  private recusarSeOutraConta(sessao: SessaoWidget): string | null {
    const host = this.emailDoHost();
    if (contaConfere(host, sessao.email)) return null;
    this.trocarConta = true;

    return `Você entrou como ${sessao.email}, mas este site está usando ${host}. Entre com essa conta.`;
  }

  /** Guarda a sessão recém-emitida e liga o que depende dela. */
  private aplicarSessao(sessao: SessaoWidget): void {
    this.sessao = sessao;
    this.trocarConta = false; // entrou com a conta certa: a próxima não precisa do seletor
    gravarSessao(this.cfg.token, sessao);
    this.ligarPoller(); // pode ser a primeira coisa a acompanhar neste browser
  }

  fechar(): void {
    this.liberarUrl();
    this.anotacoes = [];
    this.preview = null;
    this.screenshot = null;
    // O arquivo escolhido morre com o fluxo: guardá-lo faria o PRÓXIMO report nascer com o
    // anexo do anterior — o mesmo motivo pelo qual a imagem da resposta não é guardada.
    this.anexoDoReport = null;
    // Pelo mesmo motivo: o botão alternativo é a cicatriz de uma falha DESTE report, e o
    // próximo não deve nascer com ela na tela (DEV-277).
    this.printFalhou = false;
    // Volta ao REPOUSO, que no celular é o talo: quem fechou a tela não pediu o menu de novo.
    this.menuAberto = false;
    this.soltarMenu();
    this.renderRepouso();
  }

  destruir(): void {
    this.mqToque?.removeEventListener("change", this.onTrocaDeAparelho);
    this.soltarMenu();
    this.pararFoco();
    // ANTES de soltar o host: um poller vivo seguiria consultando a API com os tokens
    // desta pessoa depois de o widget ser trocado por outro (bug de identidade do DEV-106).
    this.poller?.parar();
    this.poller = null;
    // Pelo mesmo motivo do poller: um timer vivo chamaria `renderRepouso` num container
    // que já não está na página.
    this.limparAviso();
    this.liberarUrl();
    this.host.remove();
  }

  /* ---------------- Fluxo ---------------- */

  private async iniciarCaptura(): Promise<void> {
    this.printFalhou = false;
    // Celular não tem getDisplayMedia — nenhum browser mobile expõe a tela —, então lá a
    // imagem vem de RASTERIZAR a página (DEV-277). É o que dá ao celular o mesmo um-toque
    // do desktop: antes, o caminho principal caía no seletor da galeria, e a pessoa tinha
    // de sair do site, tirar o print pelo gesto do aparelho e voltar para escolhê-lo.
    if (!suportaCapturaTela()) {
      await this.rasterizarDoLauncher();

      return;
    }
    this.host.style.display = "none"; // não entrar no print
    try {
      this.screenshot = await capturarTela();
    } catch {
      this.host.style.display = "";
      // Recusar a captura não é desistir do relato, e também não vira uma pergunta: o
      // widget redesenha a página sozinho (decisão do Alexandre, 24/08/2026). Perguntar
      // custava um clique a mais para oferecer a ÚNICA saída que restava — e o que ele
      // gera é a página anfitriã, a mesma que a pessoa estava vendo, nunca outra janela.
      await this.rasterizarDoLauncher();

      return;
    }
    this.host.style.display = "";
    this.abrirReport();
  }

  /**
   * O print gerado a partir do LAUNCHER — celular (onde é o caminho normal) e desktop que
   * recusou a captura.
   *
   * O formulário abre PRIMEIRO, com o painel da imagem em espera, e a print entra nele
   * quando fica pronta. Antes disto o widget ficava parado durante o redesenho e avisava
   * por um toast; o toast some sozinho, aparece por fora da tela que interessa e, o pior,
   * não dá nada para a pessoa fazer enquanto isso. Aqui os segundos do redesenho são os
   * mesmos em que ela escreve o título e a descrição — some a espera, não só o aviso.
   */
  private async rasterizarDoLauncher(): Promise<void> {
    if (this.rasterizando) return;
    this.gerandoPrint = true;
    // `null`: quem chegou aqui está no caminho PRINCIPAL, tanto no celular quanto no
    // desktop que teve a captura recusada.
    this.abrirFormularioSemPrint(null);
    await this.gerarPrint();
  }

  /**
   * Redesenha a página e entrega a imagem ao formulário que já está aberto.
   *
   * Preencher o painel EM VEZ de repintar a tela não é economia: o redesenho leva segundos,
   * e é exatamente neles que a pessoa está digitando. Um `renderReport` aqui reconstruiria
   * o formulário sob os dedos dela — o texto voltaria (o rascunho é lido), o cursor não.
   */
  private async gerarPrint(): Promise<void> {
    if (this.rasterizando) return;
    this.rasterizando = true;
    try {
      const imagem = await rasterizarPagina();
      this.gerandoPrint = false;
      this.screenshot = imagem;
      this.preencherPainelDaPrint();
    } catch {
      this.gerandoPrint = false;
      this.printFalhou = true;
      this.renderReport(this.lerRascunho());
      this.toast("Não foi possível gerar a imagem desta tela.");
    } finally {
      this.rasterizando = false;
    }
  }

  /**
   * A imagem entra no painel que já estava na tela: troca o `src`, tira o aviso de espera e
   * liga o desenho — que até aqui estava DESLIGADO de propósito, porque marca feita sobre um
   * painel vazio seria gravada em coordenada de uma caixa que a imagem ainda vai mudar.
   */
  private preencherPainelDaPrint(): void {
    if (!this.screenshot) return;
    this.liberarUrl();
    this.screenshotUrl = URL.createObjectURL(this.screenshot);
    this.anotacoes = [];
    const wrap = this.container.querySelector<HTMLDivElement>(".imgwrap");
    const img = this.container.querySelector<HTMLImageElement>(".shot");
    if (!wrap || !img) {
      // O formulário saiu da tela no meio do caminho (fechou, ou foi para os chamados).
      return;
    }
    img.addEventListener("load", () => this.desenharMarcas(true), { once: true });
    img.hidden = false;
    img.src = this.screenshotUrl;
    this.container.querySelector(".gerando")?.remove();
    wrap.classList.add("crosshair");
    this.ligarDesenho();
    // Com a print na tela, pedir print de novo é oferta morta — some, como já sumia quando
    // a tela era repintada com a imagem pronta. O que fica é o anexo de arquivo.
    this.container.querySelectorAll(".shot-add").forEach((b) => b.remove());
    // A dica do rodapé prometia o que ainda não existia; agora existe. `[data-hint="form"]`
    // e não `.hint`: a primeira `.hint` da tela é a do campo de e-mail, e sobrescrevê-la
    // trocava a explicação do e-mail pela promessa da print.
    const dica = this.container.querySelector('[data-hint="form"]');
    if (dica) dica.textContent = this.dicaDoFormulario();
  }

  /**
   * O arquivo que vai junto do report (DEV-211) — qualquer tipo, `accept` vazio.
   *
   * O teto de tamanho é conferido AQUI, antes de subir: o servidor recusaria igual, mas só
   * depois de a pessoa esperar o upload de um arquivo grande numa conexão que pode ser
   * ruim. Tipo é o contrário — quem decide é o servidor (o sniff é do conteúdo, não da
   * extensão), e a frase que a pessoa lê é a dele.
   */
  private async escolherAnexoDoReport(): Promise<void> {
    const arquivo = await escolherArquivo();
    if (!arquivo) return; // cancelou: nada muda

    if (arquivo.size > MAX_ANEXO_BYTES) {
      this.erroDoCampo("anexo", `“${arquivo.name}” tem ${tamanhoCurto(arquivo.size)} — o limite é 20 MB.`);

      return;
    }
    this.erroDoCampo("anexo", null);
    this.anexoDoReport = arquivo;
    this.pintarAnexoDoReport();
  }

  /** O chip do arquivo escolhido, com o ✕ que desfaz. Sem arquivo, a faixa some. */
  private pintarAnexoDoReport(): void {
    const faixa = this.container.querySelector<HTMLDivElement>(".anexo-escolhido");
    const botao = this.container.querySelector<HTMLButtonElement>('[data-act="anexar-arquivo"] span');
    if (!faixa) return;

    if (!this.anexoDoReport) {
      faixa.hidden = true;
      faixa.innerHTML = "";
      if (botao) botao.textContent = "Anexar um arquivo";

      return;
    }

    // O botão passa a dizer "trocar": com um arquivo escolhido, "anexar" prometia um
    // segundo anexo — e só um viaja por report.
    if (botao) botao.textContent = "Trocar o arquivo";
    faixa.hidden = false;
    faixa.innerHTML =
      `<span class="anexo-nome" title="${escapar(this.anexoDoReport.name)}">${escapar(this.anexoDoReport.name)}</span>` +
      `<span class="anexo-tam">${tamanhoCurto(this.anexoDoReport.size)}</span>` +
      `<button class="anexo-tirar" type="button" aria-label="Remover o arquivo">${ICON_X}</button>`;
    faixa.querySelector<HTMLButtonElement>(".anexo-tirar")!.addEventListener("click", () => {
      this.anexoDoReport = null;
      this.erroDoCampo("anexo", null);
      this.pintarAnexoDoReport();
    });
  }

  /** Abre a tela de report com o screenshot atual (comum aos dois caminhos de captura). */
  private abrirReport(): void {
    if (!this.screenshot) return;
    this.liberarUrl();
    this.screenshotUrl = URL.createObjectURL(this.screenshot);
    this.anotacoes = [];
    // O sentinela do caminho PRINCIPAL, dito aqui em vez de presumido: `tipoEscolhido`
    // sobrevive à tela que o definiu, então quem passou por "Abrir chamado" e depois
    // clicou em "Reportar bug" reabria o formulário do OUTRO caminho — com o título, o
    // seletor de tipo e o tipo dele, sobre a print que acabou de tirar. É o espelho do
    // que `abrirReportSemPrint` faz: cada entrada declara o seu caminho.
    this.tipoEscolhido = null;
    this.renderReport();
  }

  /**
   * Caminho SECUNDÁRIO (DEV-144): abre o formulário direto, sem passar pela captura.
   *
   * É a primeira tela do widget que roda em CELULAR de verdade — `getDisplayMedia` é
   * desktop-only, então até aqui o formulário só existia depois de uma captura que só o
   * desktop consegue fazer. Por isso ele não pode pressupor o painel da print.
   */
  private abrirReportSemPrint(): void {
    this.printFalhou = false;
    // SEMPRE um tipo, mesmo com vários: `tipoEscolhido === null` é o sentinela do caminho
    // PRINCIPAL, e usá-lo aqui para dizer "ainda não escolheu" faria a tela se comportar
    // como o principal — sem seletor e prometendo o console, que este caminho não manda.
    // Com 2+, o primeiro nasce marcado e o seletor deixa trocar; com 1, não há o que
    // perguntar (o item do menu já decidiu).
    this.abrirFormularioSemPrint(this.cfg.tiposAdicionais[0]?.id ?? null);
  }

  /** A porta única do formulário SEM print. O `tipo` é o que declara o caminho: um id de
      `tiposAdicionais` é o secundário; `null` é o principal — e é por isso que ele é
      parâmetro, e não deduzido aqui: a captura recusada no launcher (DEV-277) também abre
      esta tela, e ali o caminho continua sendo o principal. */
  private abrirFormularioSemPrint(tipo: string | null): void {
    this.liberarUrl();
    this.screenshot = null;
    this.screenshotUrl = null;
    this.anotacoes = [];
    this.tipoEscolhido = tipo;
    this.renderReport();
  }

  /**
   * O que o botão de print promete — e é o que o APARELHO sabe fazer, não o que a tela
   * gostaria de oferecer (DEV-276). Sem `getDisplayMedia` não há captura de TELA nenhuma:
   * o que o celular entrega é a página redesenhada (DEV-277), e prometer "captura da tela"
   * ali é rótulo que não se cumpre — a pessoa clicaria para descobrir.
   */
  private rotuloDaCaptura(): string {
    return suportaCapturaTela() ? "Anexar uma captura da tela" : "Anexar um print da tela";
  }

  /**
   * O caminho ALTERNATIVO, revelado só depois que o primário falhou — e **só no celular**.
   *
   * No desktop não existe botão: recusar a captura já cai no redesenho sozinho, e um botão
   * ali seria oferecer com um clique o que o widget acabou de fazer sem nenhum. No celular
   * ele é a única saída possível quando o redesenho não alcança a tela (iframe de outra
   * origem, canvas sem CORS), e não pode ser automático — abrir o seletor de arquivo exige
   * um gesto, que o `await` da tentativa anterior já consumiu.
   */
  private mostraEscapeDePrint(): boolean {
    return this.printFalhou && !suportaCapturaTela();
  }

  /** Captura a print DEPOIS de o formulário já estar aberto (botão do caminho secundário). */
  private async capturarNoFormulario(): Promise<void> {
    const rascunho = this.lerRascunho();
    // `getDisplayMedia` é desktop-only, e ESTE é o caminho que roda em celular de verdade
    // (DEV-144). Lá o print é a página rasterizada (DEV-277) — antes do DEV-276 este
    // clique só rendia um toast de erro, sem alternativa nenhuma.
    if (!suportaCapturaTela()) {
      await this.rasterizarNoFormulario(rascunho);

      return;
    }
    // Sair da tela antes do frame, como no caminho do launcher (DEV-247). Aqui o que
    // estava por cima era o FORMULÁRIO INTEIRO, tapando justamente a parte da tela que a
    // pessoa abriu o formulário para mostrar. O rascunho já foi lido acima e o DOM segue
    // de pé — `display:none` esconde, não desmonta —, então esconder não custa o texto.
    this.host.style.display = "none";
    try {
      this.screenshot = await capturarTela();
    } catch {
      // O widget volta à tela ANTES do fallback, e não no `finally`: o `await` abaixo dura
      // os segundos do redesenho, e o `finally` só correria depois deles — a pessoa
      // esperaria a imagem olhando para o site sem widget nenhum, com o formulário que ela
      // acabou de preencher escondido atrás de um `display:none`.
      this.host.style.display = "";
      // Mesma regra do launcher: recusou a captura, o widget redesenha a página sozinho. O
      // rascunho volta à tela porque `renderReport` o repõe, e a espera acontece com o
      // painel da imagem já montado.
      this.gerandoPrint = true;
      this.renderReport(rascunho);
      await this.gerarPrint();

      return;
    } finally {
      // No `finally` porque a volta à tela vale nos DOIS desfechos: sem ela, cancelar a
      // captura deixaria o widget invisível com o formulário (e o relato) preso dentro.
      this.host.style.display = "";
    }
    this.mostrarPrint(rascunho);
  }

  /**
   * A página redesenhada, com o formulário já aberto: o mesmo `rasterizarPagina` do
   * launcher, mas devolvendo o rascunho à tela. Serve aos DOIS aparelhos — é o primário do
   * celular e o escape do desktop —, porque o que muda entre eles é quem chama, não o que
   * acontece depois.
   */
  private async rasterizarNoFormulario(rascunho: { titulo: string; descricao: string; email: string }): Promise<void> {
    if (this.rasterizando) return;
    // Repinta UMA vez, para o painel da imagem nascer em espera; daí em diante a imagem
    // entra nele sem tocar no resto da tela.
    this.gerandoPrint = true;
    this.renderReport(rascunho);
    await this.gerarPrint();
  }

  /**
   * O print do celular: o seletor nativo já filtrado em imagem — um screenshot tirado no
   * aparelho ou uma foto —, normalizado para o MESMO WebP da captura de desktop.
   *
   * Desistir (ou uma imagem ilegível) NÃO repinta a tela: o formulário continua de pé com
   * o que já estava digitado, porque nada saiu do DOM — e um re-render aqui só arriscaria
   * o rascunho para não mostrar novidade alguma.
   */
  private async imagemNoFormulario(rascunho: { titulo: string; descricao: string; email: string }): Promise<void> {
    const arquivo = await escolherImagem();
    if (!arquivo) return;
    try {
      this.screenshot = await normalizarImagem(arquivo);
    } catch {
      this.toast("Não foi possível ler a imagem.");

      return;
    }
    this.mostrarPrint(rascunho);
  }

  /** A print entrou no formulário aberto: troca a objectURL, zera as marcas da anterior e
      repinta a tela com o rascunho. Os dois caminhos (frame e arquivo) terminam aqui. */
  private mostrarPrint(rascunho: { titulo: string; descricao: string; email: string }): void {
    this.liberarUrl();
    this.screenshotUrl = this.screenshot === null ? null : URL.createObjectURL(this.screenshot);
    this.anotacoes = [];
    this.renderReport(rascunho);
  }

  /** O que a pessoa já digitou, para sobreviver a um re-render do formulário. */
  private lerRascunho(): { titulo: string; descricao: string; email: string } {
    const val = (sel: string): string =>
      this.container.querySelector<HTMLInputElement | HTMLTextAreaElement>(sel)?.value ?? "";

    return {
      titulo: val("#altbug-titulo"),
      descricao: val("#altbug-desc"),
      email: val("#altbug-email"),
    };
  }

  /**
   * Mostra (ou apaga) o erro de um campo do formulário. Fica JUNTO do campo, não num toast:
   * o toast some, e o que a pessoa precisa é ler a explicação com o rascunho ainda na tela —
   * mesma razão do erro de envio no composer do front.
   */
  private erroDoCampo(campo: string, msg: string | null): boolean {
    const p = this.container.querySelector<HTMLParagraphElement>(`[data-erro="${campo}"]`);
    if (!p) return false;
    p.textContent = msg ?? "";
    p.hidden = msg === null;

    return true;
  }

  /**
   * Onde desenhar cada recusa do 422 do intake.
   *
   * Antes, TODO 422 ia para o campo de e-mail — e `erroDoCampo` desiste em silêncio quando
   * o alvo não existe, que é exatamente o caso dos hosts que informam o e-mail no init (o
   * próprio Tasks é um deles). A pessoa clicava em Enviar, o botão reabilitava e nada
   * aparecia, com o relato inteiro preso na tela. Já errava antes deste card — título
   * acima de 80 caracteres mostrava o erro do título embaixo de "E-mail" —, e as regras do
   * DEV-144 acrescentaram duas causas prováveis (tipo fora da allowlist, print condicional).
   *
   * O fallback é o toast, e não o silêncio: campo que a tela não tem (`screenshot` no
   * caminho principal, `dados.tipo` quando o time acabou de mexer na config) precisa
   * aparecer em algum lugar, mesmo que sem âncora.
   */
  private mostrarErroDeEnvio(r: { mensagem?: string; errosPorCampo?: Record<string, string> }): void {
    const POR_CAMPO: Record<string, string> = {
      "dados.reporter.email": "email",
      "dados.titulo": "titulo",
      "dados.descricao": "descricao",
      // `screenshot` e `dados.tipo` caem no erro do FORMULÁRIO: não há campo para eles na
      // tela, e é justamente aí que a versão antiga ficava muda.
      screenshot: "form",
      "dados.tipo": "form",
      "dados.anotacoes": "form",
      // O anexo TEM lugar na tela (DEV-211): a recusa de tipo/tamanho aparece junto do
      // arquivo escolhido, não num toast que some antes de a pessoa entender qual arquivo
      // o servidor não quis.
      anexo: "anexo",
    };

    const erros = r.errosPorCampo ?? {};
    const chaves = Object.keys(erros);

    // Sem `errors` no corpo (erro que não é de validação): só a mensagem do servidor.
    if (chaves.length === 0) {
      this.toast(r.mensagem ?? "Não foi possível enviar o report.");

      return;
    }

    let ancorou = false;
    let primeiroSemAncora: string | null = null;
    for (const chave of chaves) {
      const msg = erros[chave] ?? "";
      const alvo = POR_CAMPO[chave];
      if (alvo !== undefined && msg !== "" && this.erroDoCampo(alvo, msg)) {
        if (!ancorou) {
          this.container.querySelector<HTMLElement>(`#altbug-${alvo}`)?.focus();
          ancorou = true;
        }
      } else if (primeiroSemAncora === null && msg !== "") {
        primeiroSemAncora = msg;
      }
    }

    // O que não coube em campo nenhum vai para o toast. Se NADA ancorou, o toast é a única
    // superfície — e é ele que impede o envio de falhar sem sintoma.
    if (primeiroSemAncora !== null && !ancorou) {
      this.toast(primeiroSemAncora);
    }
  }

  private async enviar(titulo: string, descricao: string, email: string): Promise<void> {
    // Sem print só vale no caminho SECUNDÁRIO (DEV-144). No principal a print é a razão de
    // ser da tela, e o servidor a exige — sair daqui em silêncio é o comportamento antigo.
    if (this.enviando) return;
    if (!this.screenshot && this.tipoEscolhido === null) return;

    // Validação de ENTRADA, para a pessoa não escrever o relato inteiro e descobrir a regra
    // num 422 no fim. Ela não substitui o servidor: o intake recusa igual (é ele a
    // autoridade, e o `aceitaAnonimo` daqui é best-effort — se o `GET /config` falhou, o
    // widget acha que aceita).
    // Com login, o e-mail do chamado é o da SESSÃO e o formulário nem tem campo — validar
    // (ou mandar) o que o host alegou seria discutir uma identidade que o servidor já
    // descartou.
    const identificado = this.identificadoPorLogin();
    const emailFinal = identificado ? "" : email.trim();
    if (!identificado && !this.cfg.aceitaAnonimo && emailFinal === "") {
      this.erroDoCampo("email", "Informe seu e-mail para abrir o chamado.");
      this.container.querySelector<HTMLInputElement>("#altbug-email")?.focus();

      return;
    }
    // Formato só é conferido quando há algo digitado — campo vazio no modo opcional é
    // legítimo, e o teste do `@` seria um "e-mail inválido" para quem não quis informar.
    if (emailFinal !== "" && !emailParece(emailFinal)) {
      this.erroDoCampo("email", "Esse e-mail não parece válido.");
      this.container.querySelector<HTMLInputElement>("#altbug-email")?.focus();

      return;
    }
    this.erroDoCampo("email", null);

    this.enviando = true;
    this.setBotaoEnviarEstado(true);
    const tituloFinal = titulo.trim() || "Bug reportado pelo widget";
    // O nome continua vindo só do host (o formulário não o pede: quem responde precisa de um
    // endereço, não de um nome). Sem e-mail nenhum, o bloco `reporter` inteiro fica fora,
    // como antes — é isso que o back lê como report anônimo.
    const reporter =
      emailFinal !== ""
        ? { ...(this.cfg.reporter ?? {}), email: emailFinal }
        : this.cfg.reporter?.nome
          ? { nome: this.cfg.reporter.nome }
          : undefined;
    try {
      const r = await enviarReport(
        this.cfg,
        this.screenshot,
        {
        titulo: tituloFinal,
        descricao: descricao.trim(),
        ...(this.tipoEscolhido ? { tipo: this.tipoEscolhido } : {}),
        // Sem print não há marcas — e o servidor recusa a combinação, porque os comentários
        // delas iriam para o contexto.md sem imagem a que se referir.
        anotacoes: this.screenshot ? this.anotacoes.filter(anotacaoValida) : [],
        // O console acompanha só o caminho principal. O servidor zera de qualquer jeito
        // (é ele a autoridade); não mandar evita subir log que seria descartado.
        console: this.cfg.consoleHabilitado && this.tipoEscolhido === null ? this.consoleBuffer.snapshot() : [],
          meta: coletarMeta(this.cfg.contexto),
          reporter,
        },
        this.anexoDoReport,
        // A sessão vai sempre que existe: é ela que prova quem abre o chamado quando o
        // relator exige login, e o servidor a ignora quando não exige.
        this.sessao?.sessao,
      );
      if (r.ok) {
        // Só no SUCESSO: guardar um e-mail que o servidor recusou faria o próximo report
        // nascer com o valor errado preenchido.
        gravarReporterEmail(this.cfg.token, emailFinal);
        this.renderSucesso(r.codigo, r.portalUrl, r.chamadoToken, tituloFinal);
      } else if (r.status === 422) {
        // O servidor é a autoridade: a frase que a pessoa lê é a DELE, e o campo em que ela
        // aparece sai do `errors` do corpo — não de um palpite nosso. Ver mostrarErroDeEnvio.
        this.mostrarErroDeEnvio(r);
      } else if (r.status === 429) {
        this.toast(`Muitos envios. Tente de novo em ${r.retryAfter ?? 60}s.`);
      } else if (r.status === 401 && this.cfg.exigeLogin) {
        // A sessão venceu (ou nunca valeu) entre abrir a tela e enviar. Não é "widget não
        // autorizado": é login, e a saída é entrar de novo — de volta à porta, com o
        // relato ainda a um passo. A frase é do servidor, que é quem sabe o motivo.
        this.sessao = null;
        this.locais = esquecerSessao(this.cfg.token);
        this.rascunhoPendente = { titulo, descricao, email };
        this.renderEntrar("report", r.mensagem);
      } else if (r.status === 401 || r.status === 403) {
        this.toast("Widget não autorizado neste site.");
      } else {
        this.toast(`Não foi possível enviar (erro ${r.status}).`);
      }
    } catch {
      this.toast("Falha de rede ao enviar o report.");
    } finally {
      this.enviando = false;
      this.setBotaoEnviarEstado(false);
    }
  }

  /* ---------------- Render ---------------- */

  /**
   * Estado de repouso, nas três formas do DEV-222: o launcher inteiro, o TALO (o mesmo
   * launcher encolhido numa faixa fina, no lugar em que ele estava) ou nada.
   *
   * "Nada" ficou sendo só o que o HOST pediu, por `data-altbug-hotkey`. Esconder pelo menu
   * cai no talo — sumir de vez era um beco sem saída no celular, onde a tecla `?` que
   * desfazia o gesto não existe.
   */
  private renderRepouso(): void {
    const forma = this.forma();
    if (forma === "sumido") {
      this.container.innerHTML = "";
      return;
    }
    this.renderFab(forma);
  }

  /**
   * O launcher: o botão de REPORTAR, como sempre foi — clique abre a captura, e o hover
   * expande o rótulo. O que o mesmo hover levanta é um MENU colado nele, formando um corpo
   * só: o launcher é a última linha (a mais próxima do cursor, porque é a ação mais comum)
   * e as outras ações empilham do lado de dentro da tela.
   *
   * Por que o report continua sendo a linha e não um item do menu: ele é o caminho
   * principal e não pode custar dois cliques. Um menu no lugar do rótulo tirava a ação
   * principal de onde ela estava e obrigava a escolher antes de fazer a coisa mais comum.
   *
   * "Esconder" é uma LINHA do menu, não mais uma bolha com ✕ no canto: a bolha estourava o
   * retângulo do launcher (fica meio fora da tela quando ele é arrastado para a borda),
   * disputava o canto com a badge de respostas novas — as duas tinham de se alternar no
   * hover para não parecerem dois avisos — e um ✕ solto não diz o que faz. Fica na ponta
   * mais distante do cursor, que é onde deve morar a ação que ninguém quer por acidente.
   *
   * "Meus chamados" existe SEMPRE, com esse nome, esteja a pessoa logada ou não. Antes a
   * linha não aparecia sem chamado guardado — o que evitava prometer uma lista vazia, mas
   * deixava sem caminho nenhum quem trocou de aparelho ou limpou o storage: o login mora
   * DENTRO desse painel, o clique no launcher vai para a captura e a tecla '?' só devolve o
   * botão. Quem explica o estado é o painel, que abre com o texto de lista vazia e o "Entrar
   * para ver todos" — o item do menu é o nome de um lugar, e nome de lugar não muda.
   *
   * `<div role=button>` no launcher, e não `<button>`: ele contém outros botões (os itens
   * do menu), e aninhar `<button>` é inválido.
   *
   * **O TALO é este mesmo elemento com uma classe a mais** (DEV-222), e não um nó próprio.
   * Foi o que permitiu encolher o launcher sem um segundo renderizador: a posição
   * arrastada (`aplicarPosFab`), o arraste, o balão IRMÃO e o `pointer-events: auto` — que
   * só o `.fab` tem, porque o host é `pointer-events: none` — continuam sendo os de sempre.
   * O CSS encolhe apenas a PINTURA e mantém a caixa em 44x44, o retângulo que os cálculos
   * de posição medem, então o launcher volta exatamente de onde a pessoa o deixou.
   *
   * A marcação do menu vem junto, escondida: é ela que sustenta as duas asserções
   * não-nulas logo abaixo, e `display: none` não deixa parada de tabulação nenhuma.
   */
  private renderFab(forma: FormaDoLauncher): void {
    const talo = forma === "talo";
    const posClass = this.fabPos ? "" : `pos-${this.cfg.posicao}`;
    // Ícone ancora no canto; o rótulo cresce para o CENTRO da tela. À direita (default
    // bottom-right ou arrastado p/ a metade direita) → expande à esquerda.
    const ancoraDireita = this.fabPos ? this.fabPos.lado === "right" : /right$/.test(this.cfg.posicao);
    const expClass = ancoraDireita ? "exp-left" : "exp-right";
    // Sem posição arrastada, a classe de canto (`pos-*`) é a única pista da altura: topo → 0,
    // rodapé → fim da tela.
    const topoAtual = this.fabPos ? this.fabPos.top : /^top/.test(this.cfg.posicao) ? 0 : window.innerHeight;
    const ladoClass = this.ladoDoMenu(topoAtual);

    const novas = naoVistas(this.locais, this.remotos);
    // O talo TAMBÉM leva badge (DEV-224). No DEV-222 ele não levava, e a razão era boa —
    // sem adorno que dependa da contagem, o tick de 15s nunca precisava repintar um nó
    // focado. Só que ali o talo era o estado de quem tinha escondido o widget; agora ele é o
    // REPOUSO do celular, e repouso sem badge é notificação que não chega.
    const badge = novas > 0
      ? `<span class="fab-badge" aria-hidden="true">${novas > 9 ? "9+" : novas}</span>`
      : "";
    const base = talo ? ROTULO_TALO : this.cfg.texto;
    const rotulo = novas > 0 ? `${base} — ${novas} resposta(s) nova(s)` : base;

    // A contagem repete na linha: quem viu a badge precisa saber ONDE está a novidade.
    const contador = novas > 0
      ? `<span class="fab-menu-badge">${novas > 9 ? "9+" : novas}</span>`
      : "";
    // A linha existe SEMPRE, e o rótulo é sempre o mesmo. Antes ela sumia sem chamado
    // guardado — e como o login mora DENTRO desse painel, quem trocou de aparelho ou limpou o
    // storage ficava sem caminho nenhum (o clique no launcher vai para a captura, e a tecla
    // '?' só mostra o botão). Tentei um rótulo alternativo ("Entrar e ver meus chamados") para
    // não prometer uma lista vazia; o nome do lugar é "Meus chamados" e trocá-lo faz o mesmo
    // item parecer dois. Quem explica o estado é o painel: ele já abre com o texto de lista
    // vazia e o "Entrar para ver todos".
    const linhaChamados =
      `<button class="fab-menu-item" type="button" data-act="ver-chamados">${ICON_CAIXA}` +
      `<span class="fab-menu-txt">Meus chamados</span>${contador}</button>`;
    // Segundo caminho (DEV-144): existe só quando o time configurou tipos adicionais. Sem
    // eles a lista é vazia e o menu fica exatamente como era — um caminho só, pela print.
    const linhaChamadoNovo = this.cfg.tiposAdicionais.length
      ? `<button class="fab-menu-item" type="button" data-act="abrir-chamado">${ICON_CHAT_NOVO}` +
        `<span class="fab-menu-txt">${escapar(this.cfg.rotuloSecundario)}</span></button>`
      : "";
    // O atalho para a configuração DESTE relator (DEV-223). Existe só quando a sessão traz
    // a `configUrl` — e ela só vem para quem é ADMIN da org dona do relator, decidido no
    // servidor. Um cliente do site anfitrião nunca vê este item, e o admin que não entrou
    // pelo widget também não: sem login não há como saber quem está do outro lado.
    const linhaConfig = this.sessao?.configUrl
      ? `<button class="fab-menu-item" type="button" data-act="configurar">${ICON_ENGRENAGEM}` +
        `<span class="fab-menu-txt">Configurar relator</span></button>`
      : "";
    // Ordem no DOM = de fora para dentro: "esconder" é a ponta mais distante do launcher, e
    // "abrir chamado" fica encostado nele — as duas AÇÕES (reportar e abrir) ficam juntas, e
    // "meus chamados", que é ir para uma lista, fica acima delas. Com o menu abaixo o CSS
    // inverte a coluna inteira. "Configurar relator" fica logo depois de "esconder", na
    // ponta longe: é a única linha que TIRA a pessoa do site anfitrião, e é a mais rara —
    // uma por muitos reports, e só para quem administra.
    const menu =
      `<div class="fab-menu">` +
      `<button class="fab-menu-item" type="button" data-act="esconder">${ICON_X}<span class="fab-menu-txt">Esconder</span></button>` +
      linhaConfig +
      linhaChamados +
      linhaChamadoNovo +
      `</div>`;

    // `aberto` é o menu levantado por TOQUE: no desktop quem o abre é o `:hover` do CSS, e
    // esta classe nunca aparece lá (ver `formaDoLauncher`).
    const abertoClass = this.menuAberto ? " aberto" : "";
    this.container.innerHTML =
      `<div class="fab ${posClass} ${expClass} ${ladoClass}${talo ? " talo" : ""}${abertoClass}" role="button" tabindex="0"` +
      ` aria-label="${escapar(rotulo)}"${talo ? ` title="${escapar(ROTULO_TALO)}"` : ""}>` +
      menu +
      `<div class="fab-acao">` +
      `${iconeDe(this.cfg.icone)}` +
      `<span class="fab-label">${escapar(this.cfg.texto)}</span>` +
      `</div>` +
      badge +
      `</div>` +
      // IRMÃO do launcher, e depois dele no DOM: é o que permite ao CSS apagá-lo enquanto o
      // painel está aberto (`.fab:hover ~ .av-balao`) sem que o cursor indo até ele reabra o
      // painel — como filho, isso vira um piscar sem fim.
      (this.aviso !== null ? this.htmlDoAviso(this.aviso) : "");

    const fab = this.container.querySelector<HTMLDivElement>(".fab")!;
    if (this.fabPos) this.aplicarPosFab(fab);
    // `pointerdown` também é contido: o launcher inicia ARRASTE nesse evento, e sem isto um
    // clique num item viraria um gesto no botão de baixo.
    fab.querySelectorAll<HTMLButtonElement>(".fab-menu-item").forEach((item) => {
      item.addEventListener("pointerdown", (e) => e.stopPropagation());
    });
    // Todo item do menu fecha o menu — e o `fecharMenu` DEPOIS da ação é o que decide se
    // isso custa um repaint: quem abriu uma tela já pintou por cima (e ali ele não repinta
    // nada), quem só saiu numa aba nova ("Configurar relator") volta ao talo por aqui. No
    // desktop o método inteiro é um no-op, porque quem abre o menu lá é o `:hover`.
    const acaoDoMenu = (nome: string, fn: () => void): void => {
      fab.querySelector<HTMLButtonElement>(`[data-act="${nome}"]`)?.addEventListener("click", (e) => {
        e.stopPropagation();
        fn();
        this.fecharMenu();
      });
    };
    acaoDoMenu("esconder", () => this.esconder());
    acaoDoMenu("ver-chamados", () => this.abrirPainel());
    acaoDoMenu("abrir-chamado", () => this.abrirCaminhoSecundario());
    acaoDoMenu("configurar", () => this.abrirConfig());
    this.ligarArrasteFab(fab);
    this.ligarAviso(fab);
    fab.addEventListener("keydown", (e) => {
      // Enter/Espaço num item do menu é do ITEM: sem esta guarda o evento borbulhava até
      // aqui e o `preventDefault` engolia o clique do botão — pelo teclado, "esconder"
      // abria a captura.
      if ((e.target as HTMLElement).closest(".fab-menu")) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        // `<div role="button">` não dispara `click` por Enter/Espaço sozinho, então o
        // teclado precisa repetir aqui a escolha que o toque faz no `soltar` do arraste.
        if (talo) this.abrirMenu(true);
        else this.abrir();
      }
    });
  }

  /**
   * O balão em si: quem respondeu como prefixo da frase, a mensagem em até 3 linhas (o
   * corte é do CSS) e, embaixo, de que chamado é.
   *
   * `<div role="button">`, como o launcher, porque ele contém outro botão (o ✕) e aninhar
   * `<button>` é inválido. O `aria-label` carrega a mensagem inteira: com `aria-live` num
   * nó que ACABOU de entrar, é o rótulo que o leitor de tela anuncia — e um "resposta nova"
   * sem a resposta obrigaria a abrir a conversa para saber do que se trata, que é
   * exatamente o custo que este balão existe para tirar.
   */
  private htmlDoAviso(a: AvisoDeResposta): string {
    const quando = horaCurta(a.quando, new Date());
    const meta = quando === "" ? escapar(a.codigo) : `${escapar(a.codigo)} · ${escapar(quando)}`;
    const rotulo = `Resposta de ${a.autor} em ${a.codigo}: ${a.texto}. Abrir a conversa.`;

    return (
      `<div class="av-balao" role="button" tabindex="0" aria-live="polite" aria-label="${escapar(rotulo)}">` +
      `<button class="av-fechar" type="button" data-act="dispensar-aviso" aria-label="Dispensar aviso">${ICON_X}</button>` +
      `<p class="av-txt"><span class="av-quem">${escapar(a.autor)}:</span> ${escapar(a.texto)}</p>` +
      `<p class="av-meta">${meta}</p>` +
      `</div>`
    );
  }

  /** Posiciona o balão e liga os cliques. Chamado a cada `renderFab`, porque o balão é
      repintado junto com o launcher (a badge muda, o HTML inteiro é refeito). */
  private ligarAviso(fab: HTMLDivElement): void {
    const balao = this.container.querySelector<HTMLDivElement>(".av-balao");
    if (!balao) return;
    this.posicionarAviso(fab, balao);

    const abrir = (): void => {
      const codigo = this.aviso?.codigo;
      // Limpa SEM repintar: quem vai pintar por cima é a tela do chamado.
      this.limparAviso();
      if (codigo !== undefined) void this.abrirChamado(codigo);
    };
    balao.addEventListener("click", abrir);
    balao.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        abrir();
      }
    });
    balao.querySelector<HTMLButtonElement>('[data-act="dispensar-aviso"]')!.addEventListener("click", (e) => {
      e.stopPropagation(); // senão dispensar também abriria a conversa
      this.dispensarAviso();
    });
  }

  /**
   * Cola o balão no launcher, do lado em que ele estiver — o botão é arrastável, e um aviso
   * ancorado num canto fixo apontaria para o nada.
   *
   * A posição é lida do rect do launcher, mas gravada nas MESMAS bordas que ele usa
   * (`right`/`bottom` embaixo à direita, `left`/`top` em cima à esquerda): assim os dois
   * acompanham o redimensionamento da janela juntos, sem listener de resize para manter.
   * Na vertical vale a regra do menu — launcher na metade de baixo, balão por cima; na
   * metade de cima, por baixo, senão ele sairia pela borda superior.
   */
  private posicionarAviso(fab: HTMLDivElement, balao?: HTMLDivElement | null): void {
    const alvo = balao ?? this.container.querySelector<HTMLDivElement>(".av-balao");
    if (!alvo) return;
    const r = fab.getBoundingClientRect();

    const direita = r.left + r.width / 2 > window.innerWidth / 2;
    alvo.classList.toggle("av-dir", direita);
    alvo.classList.toggle("av-esq", !direita);
    if (direita) {
      alvo.style.right = `${Math.max(8, window.innerWidth - r.right)}px`;
      alvo.style.left = "auto";
    } else {
      alvo.style.left = `${Math.max(8, r.left)}px`;
      alvo.style.right = "auto";
    }

    const acima = this.ladoDoMenu(r.top) === "menu-acima";
    alvo.classList.toggle("av-acima", acima);
    alvo.classList.toggle("av-abaixo", !acima);
    if (acima) {
      alvo.style.bottom = `${Math.max(8, window.innerHeight - r.top + GAP_AVISO)}px`;
      alvo.style.top = "auto";
    } else {
      alvo.style.top = `${Math.max(8, r.bottom + GAP_AVISO)}px`;
      alvo.style.bottom = "auto";
    }
  }

  /** Põe o balão na tela e marca o relógio da saída. */
  private mostrarAviso(aviso: AvisoDeResposta): void {
    this.limparAviso();
    this.aviso = aviso;
    this.avisoTimer = setTimeout(() => this.dispensarAviso(), MS_AVISO);
    this.renderRepouso();
  }

  /** Só o estado (e o relógio). Quem for repintar chama `dispensarAviso`. */
  private limparAviso(): void {
    if (this.avisoTimer !== null) {
      clearTimeout(this.avisoTimer);
      this.avisoTimer = null;
    }
    this.aviso = null;
  }

  /**
   * Tira o balão — pelo ✕ ou pelo tempo. O que ele avisava NÃO se perde: a contagem segue
   * na badge e na linha "Meus chamados", e é isso que autoriza ele a sumir sozinho.
   */
  private dispensarAviso(): void {
    if (this.aviso === null) return;
    this.limparAviso();
    if (this.emRepouso()) this.renderRepouso();
  }

  /**
   * Diálogo de esconder — **só no desktop** (DEV-224). Esconder já está decidido (foi o que
   * se pediu); o que a pergunta resolve é o PRÓXIMO load.
   *
   * A dica é o que impede a resposta "não" de ser um caminho sem volta, e por isso ela é
   * literal de novo: no celular, onde o `?` não existe, a pergunta não é feita — quem chega
   * aqui tem teclado.
   */
  private renderEsconder(): void {
    this.container.innerHTML = `
      <div class="overlay">
        <div class="review esconder">
          <h2>Reaparecer quando recarregar?</h2>
          <p class="esc-tip">
            Você sempre pode utilizar o atalho <kbd class="esc-tecla">${exibirHotkey(this.cfg.hotkeyCombo)}</kbd> para exibi-lo novamente.
          </p>
          <div class="actions">
            <button class="btn" type="button" data-act="ocultar-sempre">Não</button>
            <button class="btn primary" type="button" data-act="ocultar-agora">Sim</button>
          </div>
        </div>
      </div>`;

    const esconder = (voltar: boolean): void => {
      gravarOculto(this.cfg.token, !voltar);
      this.esconderLauncher();
    };
    const acao = (nome: string, fn: () => void): void => {
      this.container.querySelector<HTMLButtonElement>(`[data-act="${nome}"]`)!.addEventListener("click", fn);
    };
    acao("ocultar-sempre", () => esconder(false));
    acao("ocultar-agora", () => esconder(true));
  }

  /**
   * Para que lado o menu abre: launcher na metade de CIMA da tela → abre para BAIXO, senão
   * sairia pela borda superior, onde não há como alcançá-lo.
   *
   * Vive num método porque a decisão acontece em dois momentos — no render e ao soltar o
   * arrasto. Enquanto só o render decidia, arrastar o launcher para o topo o deixava com o
   * menu ainda abrindo para cima, isto é, fora da tela.
   */
  private ladoDoMenu(top: number): "menu-acima" | "menu-abaixo" {
    return top < window.innerHeight / 2 ? "menu-abaixo" : "menu-acima";
  }

  /** Arrasto do FAB: acima do limiar vira mover (persiste a posição); abaixo, é
      clique → abre. Os itens do menu cuidam do próprio clique (não iniciam arrasto). */
  private ligarArrasteFab(fab: HTMLDivElement): void {
    let origem: { x: number; y: number; left: number; top: number } | null = null;
    let arrastou = false;
    fab.addEventListener("pointerdown", (e) => {
      if ((e.target as HTMLElement).closest(".fab-menu")) return; // menu tem seus cliques
      e.preventDefault();
      const r = fab.getBoundingClientRect();
      origem = { x: e.clientX, y: e.clientY, left: r.left, top: r.top };
      arrastou = false;
      fab.setPointerCapture(e.pointerId);
    });
    fab.addEventListener("pointermove", (e) => {
      if (!origem) return;
      if (!arrastou) {
        if (Math.hypot(e.clientX - origem.x, e.clientY - origem.y) < LIMIAR_ARRASTE) return;
        arrastou = true;
        // Fecha o painel: mover um card que muda de tamanho debaixo do cursor é confuso, e é
        // o círculo (não o painel) que os clamps de borda precisam medir.
        fab.classList.add("arrastando");
        // E CENTRA o círculo no ponteiro. Com o painel aberto quem se agarra é a LINHA (~150px
        // de largura, e o painel começa bem acima dela); o círculo de 44px que sobra vive na
        // ponta ancorada dessa linha, então seguir com a referência do `pointerdown` deixava
        // o launcher a até ~100px do dedo pelo resto do gesto. Grudá-lo no ponteiro é o que
        // faz o que se arrasta estar sob o cursor. A leitura do rect já enxerga o painel
        // fechado (ela força o recálculo de estilo).
        const r = fab.getBoundingClientRect();
        this.posicionarFab(fab, e.clientX - r.width / 2, e.clientY - r.height / 2);
        // Referência lida DEPOIS de posicionar: perto das bordas o clamp muda o destino, e
        // uma referência não-clampada faria o gesto acumular desvio a cada movimento.
        const pos = fab.getBoundingClientRect();
        origem = { x: e.clientX, y: e.clientY, left: pos.left, top: pos.top };
        return;
      }
      this.posicionarFab(fab, origem.left + (e.clientX - origem.x), origem.top + (e.clientY - origem.y));
    });
    const soltar = (e: PointerEvent): void => {
      if (!origem) return;
      origem = null;
      try {
        fab.releasePointerCapture(e.pointerId);
      } catch {
        /* ok */
      }
      if (arrastou) {
        // Ancora pela borda horizontal mais próxima do centro do ícone: na metade
        // direita → ancora à direita (rótulo expande p/ a esquerda) e vice-versa.
        const r = fab.getBoundingClientRect();
        const lado: "left" | "right" = r.left + r.width / 2 > window.innerWidth / 2 ? "right" : "left";
        // No talo o x não é escolha da pessoa: ele mora COLADO na lateral (DEV-224). O que o
        // arraste move ali é a ALTURA — e é isso que o mantém fora do caminho sem virar um
        // objeto solto no meio da tela do site anfitrião.
        const x = this.emTalo() ? 0 : Math.max(8, lado === "right" ? window.innerWidth - r.right : r.left);
        this.fabPos = { lado, x, top: Math.max(8, r.top) };
        this.salvarPosFab();
        fab.classList.toggle("exp-left", lado === "right");
        fab.classList.toggle("exp-right", lado === "left");
        // O menu segue o launcher: arrastado para o topo, ele passa a abrir para baixo. Sem
        // isto a classe do render ficava congelada e o menu abria fora da tela.
        const ladoMenu = this.ladoDoMenu(this.fabPos.top);
        fab.classList.toggle("menu-abaixo", ladoMenu === "menu-abaixo");
        fab.classList.toggle("menu-acima", ladoMenu === "menu-acima");
        this.aplicarPosFab(fab);
        // O balão vai junto: ele fica escondido durante o arrasto e reaparece ao soltar —
        // no canto velho, se ninguém o remedisse.
        this.posicionarAviso(fab);
        // Só agora o painel pode reabrir: a medida acima tem de ver o círculo.
        fab.classList.remove("arrastando");
      } else if (this.emTalo()) {
        // Não arrastou, e está encolhido: o toque no talo ABRE O MENU — é o hover de quem
        // está no dedo. Aqui, e não num `click` próprio, porque com o ponteiro capturado o
        // gesto termina neste `soltar`: um segundo caminho abriria o menu duas vezes.
        this.abrirMenu();
      } else {
        this.abrir(); // não arrastou → clique: reporta, como sempre foi
      }
    };
    fab.addEventListener("pointerup", soltar);
    fab.addEventListener("pointercancel", () => {
      origem = null;
      fab.classList.remove("arrastando");
    });
  }

  /** Move o FAB durante o arrasto (left/top ao vivo), limitado à viewport (margem 8px).
      O ancoramento final (por lado) é feito no drop via aplicarPosFab. */
  private posicionarFab(fab: HTMLDivElement, left: number, top: number): void {
    const w = fab.offsetWidth || 48;
    const h = fab.offsetHeight || 48;
    const x = Math.max(8, Math.min(window.innerWidth - w - 8, left));
    const y = Math.max(8, Math.min(window.innerHeight - h - 8, top));
    fab.style.left = `${x}px`;
    fab.style.top = `${y}px`;
    fab.style.right = "auto";
    fab.style.bottom = "auto";
  }

  /**
   * Fixa o FAB na posição persistida, ancorado pelo lado do ícone (`fabPos.lado`), p/ o ícone
   * ficar no canto e o rótulo expandir para o centro. Clampa à viewport.
   *
   * Na VERTICAL a âncora depende de para onde o painel abre: crescendo para cima, ancora pelo
   * `bottom` — com `top` fixo o painel empurraria a ação principal para baixo, tirando-a de
   * onde o cursor a deixou (e, no rodapé, para fora da tela). Crescendo para baixo, `top`.
   * As classes `pos-*` (sem arrasto) já seguem essa regra: as de baixo ancoram por `bottom`.
   *
   * A conta usa a altura da LINHA principal, não a do painel: ela é a mesma aberta ou
   * fechada (o ícone de 18px domina o padding), então a âncora não muda ao abrir.
   */
  private aplicarPosFab(fab: HTMLDivElement): void {
    if (!this.fabPos) return;
    const h = fab.querySelector<HTMLElement>(".fab-acao")?.offsetHeight || fab.offsetHeight || 48;
    const maxTop = Math.max(8, window.innerHeight - h - 8);
    // O talo encosta na borda (DEV-224); o launcher cheio guarda os 8px de folga de sempre.
    // A folga vira o "pulo para dentro" do momento em que o menu abre, e é ele que impede as
    // linhas do menu de nascerem coladas na moldura do celular.
    const minX = this.forma() === "talo" ? 0 : 8;
    const maxX = Math.max(minX, window.innerWidth - (fab.offsetWidth || 48) - 8);
    const x = Math.max(minX, Math.min(maxX, this.fabPos.x));
    const top = Math.max(8, Math.min(maxTop, this.fabPos.top));
    if (this.ladoDoMenu(top) === "menu-acima") {
      fab.style.bottom = `${Math.max(8, window.innerHeight - (top + h))}px`;
      fab.style.top = "auto";
    } else {
      fab.style.top = `${top}px`;
      fab.style.bottom = "auto";
    }
    if (this.fabPos.lado === "right") {
      fab.style.right = `${x}px`;
      fab.style.left = "auto";
    } else {
      fab.style.left = `${x}px`;
      fab.style.right = "auto";
    }
  }

  private salvarPosFab(): void {
    try {
      localStorage.setItem("altbug:fabpos", JSON.stringify(this.fabPos));
    } catch {
      /* localStorage indisponível — ok, só não persiste */
    }
  }

  private carregarPosFab(): { lado: "left" | "right"; x: number; top: number } | null {
    try {
      const raw = localStorage.getItem("altbug:fabpos");
      if (!raw) return null;
      const p = JSON.parse(raw) as Record<string, unknown>;
      if ((p?.lado === "left" || p?.lado === "right") && typeof p.x === "number" && typeof p.top === "number") {
        return { lado: p.lado, x: p.x, top: p.top };
      }
      // Migração do formato antigo { left, top } → ancorado à esquerda.
      if (typeof p?.left === "number" && typeof p?.top === "number") {
        return { lado: "left", x: p.left, top: p.top };
      }
      return null;
    } catch {
      return null;
    }
  }

  /* ---------------- Painel de suporte (DEV-107) ---------------- */

  /**
   * Menu do FAB: as duas ações ("Reportar um problema" / "Meus chamados") no mesmo card
   * que já lista os chamados. Um card só, em vez de um popover ancorado ao FAB — o botão
   * é arrastável, então ancorar exigiria recalcular a posição do menu a cada gesto, e a
   * lista precisa de espaço que um popover de canto não tem.
   *
   * Pinta imediatamente com o que está no localStorage (título e código, que não mudam) e
   * o resumo do servidor chega depois preenchendo situação e respostas: a lista aparece
   * cheia no primeiro frame mesmo com rede lenta.
   */
  private renderPainel(): void {
    this.liberarAnexos(); // vindo da conversa, os blobs dela ficam para trás
    const temChamados = this.locais.length > 0;
    this.container.innerHTML = `
      <div class="overlay">
        <div class="painel-caixa">
          <button class="painel-fechar" type="button" data-act="fechar" aria-label="Fechar">${ICON_X}</button>
          <div class="painel" data-tela="lista">
            <div class="painel-corpo">
              ${
                temChamados
                  ? `<div class="ch-lista">${this.htmlDaLista()}</div>`
                  : `<p class="ch-vazio">Os chamados que você abrir por aqui aparecem nesta lista, com as respostas do time.</p>`
              }
              ${this.htmlDaSessao()}
            </div>
          </div>
        </div>
      </div>`;

    this.container.querySelector<HTMLButtonElement>('[data-act="fechar"]')!.addEventListener("click", () => this.fechar());
    this.container.querySelector<HTMLButtonElement>('[data-act="entrar"]')?.addEventListener("click", () => this.entrar());
    this.container.querySelector<HTMLButtonElement>('[data-act="sair"]')?.addEventListener("click", () => this.sair());
    this.ligarLista();
  }

  /**
   * O rodapé de identidade: só o link, alinhado à direita — entrar (para ver os chamados
   * abertos em outro aparelho) ou sair. O convite que explicava o ganho ("Abriu chamados em
   * outro aparelho?") saiu: num painel sem título, duas frases de texto corrido competiam
   * com a lista, que é o conteúdo.
   */
  private htmlDaSessao(): string {
    if (this.sessao) {
      return `<p class="ch-sessao"><button class="ch-link" type="button" data-act="sair">Sair</button></p>`;
    }

    return (
      `<p class="ch-sessao">` +
      `<button class="ch-link" type="button" data-act="entrar">Entrar para ver todos</button>` +
      `</p>`
    );
  }

  /**
   * As linhas da lista, montadas do storage + do último resumo recebido.
   *
   * Os encerrados (DEV-183) saem do corpo da lista e viram uma SEÇÃO colapsada no fim: o
   * que já acabou não deve empurrar para baixo o que ainda está vivo, e quem acumula
   * chamados num site que usa todo dia estava rolando por meses de histórico para chegar ao
   * de hoje. Colapsada, não sumida: cancelado e resolvido ainda são a resposta de "o que
   * aconteceu com aquilo?", e a conversa deles continua aberta um toque adiante.
   */
  private htmlDaLista(): string {
    const { abertos, compartilhados, encerrados } = separarChamados(this.locais, this.remotos);
    const linhas = abertos.map((i) => this.htmlDaLinha(i)).join("");

    // Card que o TIME compartilhou (DEV-245). Vem numa faixa própria, ABERTA, e com um
    // título que diz de onde veio: pôr um card do time sob "Meus chamados" promete o que
    // a linha não é — o mesmo motivo pelo qual o portal tem duas abas. Sem nenhum, nem o
    // cabeçalho aparece: quem só abre chamado nunca vê a seção existir.
    const bloco =
      compartilhados.length === 0
        ? ""
        : `<p class="ch-faixa">Compartilhados com você</p>` +
          compartilhados.map((i) => this.htmlDaLinha(i)).join("");

    if (encerrados.length === 0) return linhas + bloco;

    // Sem nada em aberto a lista não pode abrir na seção fechada, ou pareceria vazia por
    // bug. A frase é curta de propósito: é estado, não instrução.
    const vazio =
      abertos.length === 0 && compartilhados.length === 0
        ? `<p class="ch-vazio">Nenhum chamado em aberto.</p>`
        : "";
    const aberta = this.encerradosAbertos;

    return (
      linhas +
      bloco +
      vazio +
      `<button class="ch-sec" type="button" data-act="encerrados" aria-expanded="${aberta}">` +
      `<span class="ch-sec-chevron ${aberta ? "aberta" : ""}">${ICON_VOLTAR}</span>` +
      `Encerrados (${encerrados.length})</button>` +
      (aberta ? encerrados.map((i) => this.htmlDaLinha(i)).join("") : "")
    );
  }

  /** Uma linha: título, código, quando, o marcador de resposta nova e o selo de situação. */
  private htmlDaLinha({ local, remoto, novas }: ItemDaLista): string {
    const selo = remoto
      ? `<span class="ch-selo ${remoto.situacao === "resolvido" ? "ok" : ""}">${escapar(remoto.situacaoLabel)}</span>`
      : "";
    const quando = dataCurta(remoto?.ultimaRespostaEm ?? local.abertoEm);
    const meta = remoto?.ultimaRespostaEm ? `Resposta em ${quando}` : `Aberto em ${quando}`;

    return (
      `<button class="ch-item" type="button" data-codigo="${escapar(local.codigo)}">` +
      `<span class="ch-txt">` +
      `<span class="ch-titulo">${escapar(remoto?.titulo ?? local.titulo)}</span>` +
      `<span class="ch-meta"><span class="ch-codigo">${escapar(local.codigo)}</span> · ${escapar(meta)}</span>` +
      `</span>` +
      (novas > 0 ? `<span class="ch-novas" aria-label="${novas} resposta(s) nova(s)">${novas}</span>` : "") +
      selo +
      `</button>`
    );
  }

  private ligarLista(): void {
    this.container.querySelectorAll<HTMLButtonElement>(".ch-item").forEach((el) => {
      el.addEventListener("click", () => {
        const codigo = el.dataset.codigo;
        if (codigo) void this.abrirChamado(codigo);
      });
    });
    // Repinta só a lista: o resto do painel (o rodapé de identidade) não muda, e
    // re-renderizar tudo tiraria o foco do botão que a pessoa acabou de acionar.
    this.container.querySelector<HTMLButtonElement>('[data-act="encerrados"]')?.addEventListener("click", () => {
      this.encerradosAbertos = !this.encerradosAbertos;
      const lista = this.container.querySelector<HTMLDivElement>(".ch-lista");
      if (!lista) return;
      lista.innerHTML = this.htmlDaLista();
      this.ligarLista();
      this.container.querySelector<HTMLButtonElement>('[data-act="encerrados"]')?.focus();
    });
  }

  /**
   * Busca o resumo e repinta a lista SE o painel ainda estiver aberto — a pessoa pode ter
   * fechado (ou ido para o fluxo de report) durante a requisição, e repintar por cima
   * arrancaria a tela debaixo dela.
   *
   * Token que o servidor não reconhece é esquecido do storage: chamado apagado ou token
   * revogado, e sem isso a linha ficaria ali para sempre dando 404 ao clique.
   */
  private async atualizarChamados(): Promise<void> {
    // O guard tem de espelhar `ligarPoller` (e `buscarResumo`): com SESSÃO há o que
    // perguntar mesmo sem nenhum chamado local. Enquanto ele olhava só `locais`, quem
    // acabava de entrar num aparelho novo caía exatamente neste `return` — nenhuma
    // consulta acontecia e o painel ficava no texto de lista vazia com um "Sair" ao lado.
    // (O tick imediato do poller até buscava, mas `sincronizarEmFundo` só repinta em
    // repouso, e o painel está aberto: a lista só aparecia fechando e reabrindo.)
    if (this.locais.length === 0 && this.sessao === null) return;
    const r = await this.consultarResumo();
    if (r.falhou) {
      this.avisoNaLista("Não foi possível atualizar seus chamados agora.");
      return;
    }
    const lista = this.container.querySelector<HTMLDivElement>(".ch-lista");
    if (lista) {
      lista.innerHTML = this.htmlDaLista();
      this.ligarLista();
      return;
    }
    // Sem `.ch-lista` no DOM e com chamados em mãos, a tela no ar é o ESTADO VAZIO e ele
    // ficou obsoleto — repinta inteiro. Só na tela da LISTA: o `.painel` também é a conversa
    // do chamado, e repintar por cima dela arrancaria a tela debaixo de quem está lendo.
    if (this.locais.length > 0 && this.telaNoAr() === "lista") {
      this.renderPainel();
    }
  }

  /** Qual das duas telas do painel está no ar (o `.painel` é o mesmo nas duas). */
  private telaNoAr(): string | null {
    return this.container.querySelector<HTMLDivElement>(".painel")?.dataset.tela ?? null;
  }

  /** Mostra um aviso acima da lista sem derrubar as linhas já pintadas (que vêm do
      storage e continuam válidas — o que falhou foi a atualização, não o chamado).

      Âncora no `.ch-vazio` quando não há lista: o estado vazio é justamente onde está
      quem nunca reportou neste browser, e é ele quem mais precisa de aviso ao tentar
      entrar. Enquanto a âncora era só o `.ch-lista`, a mensagem sumia calada aí. */
  private avisoNaLista(msg: string): void {
    const alvo =
      this.container.querySelector<HTMLElement>(".ch-lista") ??
      this.container.querySelector<HTMLElement>(".ch-vazio");
    if (!alvo || alvo.previousElementSibling?.classList.contains("ch-erro")) return;
    const p = document.createElement("p");
    p.className = "ch-erro";
    p.textContent = msg;
    alvo.parentElement?.insertBefore(p, alvo);
  }

  /**
   * Detalhe do chamado: situação, quando abriu e a thread de respostas ao cliente.
   *
   * Abrir CONTA COMO VER: gravamos `respostasVistas` com o total que o servidor acabou de
   * informar, e é isso que zera a badge. O total vem do servidor de propósito — se o time
   * excluir um comentário ele DIMINUI, e um contador local incrementado deixaria a badge
   * acesa para sempre.
   */
  private async abrirChamado(codigo: string): Promise<void> {
    const local = this.locais.find((c) => c.codigo === codigo);
    if (!local) return;

    this.renderChamado(codigo, null, "carregando");
    const chamado = await buscarChamado(this.cfg.endpoint, this.cfg.token, codigo, local.token, this.sessao?.sessao);
    // Fechou ou navegou durante a requisição: não repinta por cima.
    if (!this.container.querySelector(".painel")) return;
    if (!chamado) {
      this.renderChamado(codigo, null, "erro");
      return;
    }

    this.locais = marcarVistas(this.cfg.token, codigo, chamado.respostas);
    this.remotos = [chamado, ...this.remotos.filter((r) => r.codigo !== codigo)];
    this.renderChamado(codigo, chamado, "ok");
  }

  /**
   * O detalhe não tem barra de cabeçalho: o voltar é um chevron sem caixa à esquerda do bloco
   * título + meta, e o fechar segue como badge fora do canto. Some ~57px de chrome, que é o
   * que faltava de altura para a thread caber com o composer dentro do teto do painel.
   *
   * O que era o rótulo da barra ("BUG-41") desce para a meta: o código não é o que a pessoa
   * reconhece, o assunto é — e o assunto agora é o maior elemento da tela. Para onde o botão
   * volta vive no `aria-label`, já que um chevron sozinho não diz.
   *
   * A cabeça é montada aqui, e não em `htmlDoChamado`, porque ela existe nos TRÊS estados:
   * enquanto carrega e no erro só se sabe o código, mas o caminho de volta tem de existir —
   * antes ele vinha de graça na barra que saiu.
   */
  private renderChamado(codigo: string, chamado: Chamado | null, estado: "carregando" | "erro" | "ok"): void {
    // Esta tela tem blobs (a print do report e as imagens das respostas) e é repintada em
    // cada passo do fluxo — abrir, responder, voltar da lista. Soltar aqui, na troca, é o
    // que impede a memória da página do CLIENTE de crescer a cada volta.
    this.liberarAnexos();
    // A imagem escolhida para a resposta morre junto com o composer que a mostrava: o
    // HTML abaixo nasce com a faixa vazia, e guardar o Blob aqui o mandaria escondido na
    // próxima mensagem — anexo que ninguém vê antes de enviar.
    this.imagemDaResposta = null;

    const cabeca = (titulo: string, meta: string, selo: string): string =>
      `<div class="ch-cab">` +
      `<button class="ch-voltar" type="button" data-act="voltar" aria-label="Voltar para meus chamados">${ICON_VOLTAR}</button>` +
      `<span class="ch-pilha"><h3>${titulo}</h3>` +
      (meta ? `<p class="ch-sub">${meta}</p>` : "") +
      `</span>` +
      selo +
      `</div>`;

    const corpo =
      estado === "carregando"
        ? cabeca(escapar(codigo), "", "") + `<p class="ch-vazio">Carregando…</p>`
        : estado === "erro" || !chamado
          ? cabeca(escapar(codigo), "", "") +
            `<p class="ch-erro">Não foi possível abrir este chamado agora. Tente de novo em instantes.</p>`
          : cabeca(
              escapar(chamado.titulo),
              `${escapar(codigo)} · aberto em ${escapar(dataCurta(chamado.abertoEm))}`,
              `<span class="ch-selo ${chamado.situacao === "resolvido" ? "ok" : ""}">${escapar(chamado.situacaoLabel)}</span>`,
            ) + this.htmlDoChamado(chamado);

    this.container.innerHTML = `
      <div class="overlay">
        <div class="painel-caixa">
          <button class="painel-fechar" type="button" data-act="fechar" aria-label="Fechar">${ICON_X}</button>
          <div class="painel" data-tela="chamado" data-codigo="${escapar(codigo)}">
            <div class="painel-corpo">${corpo}</div>
          </div>
        </div>
      </div>`;

    this.container.querySelector<HTMLButtonElement>('[data-act="voltar"]')!.addEventListener("click", () => {
      this.renderPainel();
      void this.atualizarChamados();
    });
    this.container.querySelector<HTMLButtonElement>('[data-act="fechar"]')!.addEventListener("click", () => this.fechar());

    // Os binários: a print do report (na bolha que abre a conversa) e as imagens das
    // respostas. Um fetch por anexo — o token vai no header, então `<img src>` direto não
    // serve —, e o clique de cada miniatura abre a lupa (ver `carregarAnexo`).
    for (const a of chamado?.anexos ?? []) void this.carregarAnexo(codigo, a);
    for (const r of chamado?.respostasThread ?? []) {
      for (const a of r.anexos ?? []) void this.carregarAnexo(codigo, a);
    }

    // Abre no FIM da conversa. Antes o report era uma faixa de uma linha e o topo já era o
    // começo útil; agora ele é uma bolha com print, e sem isto a resposta mais recente —
    // que é o motivo de a pessoa ter aberto — nasceria abaixo da dobra.
    const rolagem = this.container.querySelector<HTMLDivElement>(".painel-corpo");
    if (rolagem && estado === "ok") rolagem.scrollTop = rolagem.scrollHeight;

    const ta = this.container.querySelector<HTMLTextAreaElement>(".ch-input");
    const enviar = () => void this.responder(codigo, ta?.value ?? "");
    this.container.querySelector<HTMLButtonElement>('[data-act="responder"]')?.addEventListener("click", enviar);
    this.container.querySelector<HTMLButtonElement>('[data-act="anexar"]')?.addEventListener("click", () => {
      void this.escolherImagemDaResposta();
    });
    // Enter envia, Shift+Enter quebra linha — o mesmo contrato do composer do front.
    ta?.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        enviar();
      }
    });
    ta?.focus();
  }

  /**
   * Envia a resposta do cliente. O rascunho só é limpo no SUCESSO — quem toma 429 ou o
   * 422 de card fechado tem de continuar com o texto na tela, junto da explicação do
   * servidor. Repinta com o chamado que o POST devolveu (que já traz a thread e a
   * contagem novas) e marca como visto, senão a própria mensagem acenderia a badge.
   */
  private async responder(codigo: string, texto: string): Promise<void> {
    const local = this.locais.find((c) => c.codigo === codigo);
    const t = texto.trim();
    if (!local || t === "" || this.enviando) return;

    this.enviando = true;
    this.setComposerEstado(true);
    const r = await enviarResposta(
      this.cfg.endpoint,
      this.cfg.token,
      codigo,
      local.token,
      t,
      this.sessao?.sessao,
      this.imagemDaResposta ?? undefined,
    );
    this.enviando = false;

    if (!this.container.querySelector(".painel")) return; // fechou durante o envio
    if (r.erro) {
      this.setComposerEstado(false);
      this.mostrarErroComposer(r.erro);
      return; // a imagem CONTINUA escolhida: quem tomou 429 tem de poder repetir o envio inteiro
    }
    // Só no sucesso, como o rascunho do texto: o servidor já a tem, e deixá-la aqui a
    // mandaria de novo na próxima mensagem.
    this.imagemDaResposta = null;
    if (r.chamado) {
      this.locais = marcarVistas(this.cfg.token, codigo, r.chamado.respostas);
      this.remotos = [r.chamado, ...this.remotos.filter((x) => x.codigo !== codigo)];
      this.renderChamado(codigo, r.chamado, "ok");
      return;
    }
    // Gravou, mas sem estado novo para pintar: recarrega do servidor.
    void this.abrirChamado(codigo);
  }

  /**
   * A imagem que o CLIENTE anexa à resposta dele (DEV-213).
   *
   * Seletor de arquivo, não `getDisplayMedia`: a captura de tela é desktop-only, e este é
   * o caminho de quem está com o problema na mão — muitas vezes num celular, onde o print
   * já está na galeria. É o mesmo par (`escolherImagem` + `normalizarImagem`) do report
   * sem print, e a normalização não é cosmética: ela reduz a foto de 12 MP da galeria a
   * algo que passa no teto do servidor, em webp.
   *
   * UMA imagem por mensagem. Não é limitação técnica: é o formato da conversa — quem tem
   * duas telas para mostrar manda duas mensagens, e cada uma fica ao lado do que explica.
   */
  private async escolherImagemDaResposta(): Promise<void> {
    const arquivo = await escolherImagem();
    if (!arquivo) return; // cancelou o seletor
    if (!this.container.querySelector(".ch-composer")) return; // navegou durante a escolha

    try {
      this.imagemDaResposta = await normalizarImagem(arquivo);
    } catch {
      this.mostrarErroComposer("Não foi possível preparar essa imagem. Tente outra.");
      return;
    }
    this.pintarImagemDaResposta();
  }

  /** A miniatura do que vai junto, com o ✕ que desfaz. Vazio = a faixa some. */
  private pintarImagemDaResposta(): void {
    const faixa = this.container.querySelector<HTMLDivElement>(".ch-anexo-escolhido");
    if (!faixa) return;

    if (!this.imagemDaResposta) {
      faixa.hidden = true;
      faixa.innerHTML = "";
      return;
    }

    const url = URL.createObjectURL(this.imagemDaResposta);
    this.urlsAnexos.push(url); // mesma vida dos outros blobs: soltos na troca de tela
    faixa.hidden = false;
    faixa.innerHTML =
      `<img src="${url}" alt="Imagem que você vai enviar" />` +
      `<button class="ch-anexo-tirar" type="button" aria-label="Remover a imagem">${ICON_X}</button>`;
    faixa.querySelector<HTMLButtonElement>(".ch-anexo-tirar")!.addEventListener("click", () => {
      this.imagemDaResposta = null;
      this.pintarImagemDaResposta();
    });
  }

  private setComposerEstado(enviando: boolean): void {
    const ta = this.container.querySelector<HTMLTextAreaElement>(".ch-input");
    const btn = this.container.querySelector<HTMLButtonElement>('[data-act="responder"]');
    if (ta) ta.disabled = enviando;
    if (btn) {
      btn.disabled = enviando;
      btn.textContent = enviando ? "Enviando…" : "Enviar";
    }
  }

  private mostrarErroComposer(msg: string): void {
    const alvo = this.container.querySelector<HTMLDivElement>(".ch-composer");
    if (!alvo) return;
    let p = alvo.querySelector<HTMLParagraphElement>(".ch-erro");
    if (!p) {
      p = document.createElement("p");
      p.className = "ch-erro";
      alvo.prepend(p);
    }
    p.textContent = msg;
  }

  private htmlDoChamado(c: Chamado): string {
    const thread = c.respostasThread ?? [];
    const mensagens = thread.length
      ? `<div class="ch-thread">${thread.map((r) => this.htmlDaMensagem(r)).join("")}</div>`
      : // `data-thread-vazia` é o que o poll procura para saber que a thread está vazia de
        // verdade (e não que a tela está em "carregando", que usa a mesma classe): é neste
        // nó que a PRIMEIRA resposta se transforma, sem repintar o resto (DEV-210).
        c.fechado
        ? `<p class="ch-vazio" data-thread-vazia>Este chamado foi encerrado sem respostas por aqui.</p>`
        : HTML_THREAD_VAZIA;

    // Composer só em chamado aberto. Fechado mostra o motivo, em vez de deixar a pessoa
    // escrever e tomar 422 depois de digitar. O `maxlength` do campo é a mesma ideia, um
    // nível abaixo: 500 é o teto do comentário do time (e da descrição do report, logo
    // acima), a fala do cliente entra na MESMA thread do card, e sem o atributo o 422
    // chegaria depois de a mensagem estar escrita (DEV-193).
    const composer = c.fechado
      ? `<p class="ch-vazio ch-composer-off">Este chamado está encerrado e não recebe novas mensagens.</p>`
      : `<div class="ch-composer">
           <textarea class="ch-input" rows="2" maxlength="500" placeholder="Escreva uma mensagem para o time…" aria-label="Sua mensagem"></textarea>
           <div class="ch-anexo-escolhido" hidden></div>
           <div class="ch-composer-acoes">
             <button class="btn ch-anexar" type="button" data-act="anexar">${ICON_CLIPE}<span>Anexar imagem</span></button>
             <button class="btn primary" type="button" data-act="responder">Enviar</button>
           </div>
         </div>`;

    // Sem título/meta/selo: quem monta a cabeça é `renderChamado`, que a precisa também nos
    // estados de carregando e erro (é lá que vive o caminho de volta).
    return this.htmlDoReport(c) + mensagens + composer;
  }

  /**
   * O report como a PRIMEIRA MENSAGEM da conversa — a fala do cliente que abre a história.
   *
   * Era uma faixa ("Seu report · 1 imagem") que NAVEGAVA para uma terceira tela. O que a
   * navegação comprava era altura: a print usava o painel inteiro e a conversa não era
   * empurrada para baixo. O que ela custava é maior: o começo da história ficava a um clique
   * e uma tela de distância, então a resposta do time — que cita a print — era lida sem ela
   * por perto, que é exatamente o problema que o DEV-173 já tinha corrigido do lado do time.
   * Aqui a print continua ganhando a tela inteira **na lupa**, a um clique, e a lupa é uma
   * camada sobre o painel: fechar devolve a conversa como ela estava, sem re-buscar nada.
   *
   * Fica FORA de `.ch-thread` de propósito: é lá que o poll costura bolha por bolha pelo
   * `data-msg` (DEV-210), e uma bolha sem par no payload sairia no primeiro tick.
   */
  private htmlDoReport(c: Chamado): string {
    const anexos = c.anexos ?? [];
    const descricao = (c.descricao ?? "").trim();
    // Sem descrição e sem anexo não há report para mostrar — e uma bolha vazia abrindo a
    // conversa diria menos que nada.
    if (descricao === "" && anexos.length === 0) return "";

    // "Você" e a data de ABERTURA: é a bolha do cliente, com o mesmo cabeçalho das outras
    // falas dele. O que a identifica como o report é a posição — ela é a primeira.
    //
    // **Menos quando o card é COMPARTILHADO** (DEV-245): ali a descrição é a nota do card,
    // escrita pelo time, e assiná-la com "Você" punha o nome da pessoa embaixo de um texto
    // que ela não escreveu — na primeira coisa que a tela mostra. Vai para o lado do time,
    // com "Do time", que é a palavra que o resto do painel já usa ("quando o time
    // responder"): não há autor no payload, e inventar um seria trocar um erro por outro.
    const doTime = c.compartilhado;

    return (
      `<div class="ch-msg ${doTime ? "do-time" : "minha"} ch-report">` +
      `<span class="ch-autor">${doTime ? "Do time" : "Você"} · ${escapar(dataCurta(c.abertoEm))}</span>` +
      (descricao !== "" ? `<span class="ch-corpo">${escapar(descricao)}</span>` : "") +
      this.htmlDosAnexos(anexos, "ch-msg-anexos") +
      `</div>`
    );
  }

  /**
   * A grade de anexos de uma tela ou de uma bolha. Vazia devolve string vazia — quem
   * chama concatena direto, sem ternário em cada ponto.
   */
  /**
   * Uma bolha da thread. Mora aqui, e não inline no `htmlDoChamado`, porque o poll insere
   * mensagem nova com a MESMA marcação (DEV-210): duas cópias divergiriam, e o que se veria
   * é a resposta que chegou pelo tick com outra cara.
   *
   * `data-msg` e `data-editado` são o que o `planejarThread` lê da tela — o id é a chave, e
   * a marca de edição diz se a bolha ainda está valendo.
   *
   * A imagem que a mensagem trouxe vai DENTRO da bolha (DEV-173): é o desenho de que o
   * texto fala, e ler um sem o outro era o problema. Mesmo par de molduras da bolha do
   * report — miniatura que amplia, chip quando não é imagem —, e o binário chega pelo
   * mesmo `carregarAnexo`.
   */
  private htmlDaMensagem(r: RespostaPublica): string {
    return (
      `<div class="ch-msg ${r.doReporter ? "minha" : ""}" data-msg="${escapar(r.id)}" data-editado="${escapar(r.editadoEm ?? "")}">` +
      `<span class="ch-autor">${escapar(r.autorNome)} · ${escapar(dataCurta(r.criadoEm))}</span>` +
      `<span class="ch-corpo">${escapar(r.texto)}</span>` +
      this.htmlDosAnexos(r.anexos ?? [], "ch-msg-anexos") +
      `</div>`
    );
  }

  private htmlDosAnexos(anexos: AnexoDoChamado[], classeExtra = ""): string {
    if (anexos.length === 0) return "";

    return `<div class="ch-anexos ${classeExtra}">${anexos.map((a) => this.htmlDoAnexo(a)).join("")}</div>`;
  }

  /** A moldura do anexo: miniatura quando é imagem, chip de arquivo quando não é. */
  private htmlDoAnexo(a: AnexoDoChamado): string {
    const eImagem = a.mime.startsWith("image/");
    if (!eImagem) {
      return (
        `<div class="ch-anexo-arq" title="${escapar(a.nome)}">` +
        `${ICON_CAIXA}<span class="ch-anexo-nome">${escapar(a.nome)}</span>` +
        `<span class="ch-anexo-tam">${tamanhoCurto(a.tamanhoBytes)}</span>` +
        `</div>`
      );
    }

    // `disabled` até o blob chegar: clique numa miniatura vazia abriria a lupa em branco.
    return (
      `<button class="ch-anexo" type="button" data-anexo="${escapar(a.id)}" disabled aria-label="Ampliar ${escapar(a.nome)}">` +
      `<span class="ch-anexo-carregando">Carregando…</span>` +
      `</button>`
    );
  }

  /**
   * Busca o binário e o pendura na miniatura. Um objectURL por anexo, guardados para
   * revogar depois: sem isso cada visita à tela vaza a imagem inteira na memória da página
   * do cliente — e é a página DELE, não a nossa, que paga.
   */
  private async carregarAnexo(codigo: string, a: AnexoDoChamado): Promise<void> {
    const local = this.locais.find((c) => c.codigo === codigo);
    const blob = await buscarAnexo(
      this.cfg.endpoint,
      this.cfg.token,
      codigo,
      a.id,
      local?.token ?? "",
      this.sessao?.sessao,
    );
    const alvo = this.container.querySelector<HTMLButtonElement>(`[data-anexo="${a.id}"]`);
    if (!alvo) return; // navegou durante a busca

    if (!blob) {
      alvo.innerHTML = `<span class="ch-anexo-carregando">Imagem indisponível</span>`;
      return;
    }

    const url = URL.createObjectURL(blob);
    this.urlsAnexos.push(url);
    alvo.innerHTML = `<img src="${url}" alt="${escapar(a.nome)}" />`;
    alvo.disabled = false;
    // As marcas vão só para a LUPA, nunca para a miniatura: ela é `object-fit: cover`, ou
    // seja RECORTA a imagem — pino em fração da imagem sairia deslocado ali.
    alvo.addEventListener("click", () => this.ampliar(url, a.nome, a.anotacoes));
  }

  /**
   * A lupa: a imagem em tamanho natural sobre o painel, com as MARCAS do report em cima
   * (DEV-125).
   *
   * É um elemento ACRESCENTADO ao overlay, não uma tela nova: fechar remove só ele e a
   * CONVERSA continua atrás, com os blobs já carregados, o rascunho e a rolagem no lugar.
   * É isso que deixa a print morar na bolha sem custar a tela inteira: quem quer olhar de
   * perto abre a lupa e volta de onde saiu. Re-renderizar traria de volta o "Carregando…" e
   * um `fetch` por anexo a cada abrir e fechar.
   *
   * Sem as marcas, a lupa mostrava a print CRUA enquanto a descrição, na bolha, listava
   * "Comentários / 1. … 2. …" — números que não ancoravam em nada. Só o anexo da print traz
   * `anotacoes` (o servidor as amarra ao `screenshot_anexo_id`), então anexo que o time colou
   * depois abre sem marca, como antes.
   *
   * A `<img>` vai dentro de um `.imgwrap` porque é ELE que define o sistema de coordenadas:
   * as camadas são `inset: 0` do wrap, e as coords são frações da IMAGEM. Como o wrap é um
   * item de grid centralizado, ele encolhe junto com a imagem (que é limitada por
   * `max-width/max-height` em `.ch-lupa img`) — se fosse a imagem a encolher dentro de um
   * wrap maior, todas as marcas derivariam.
   */
  private ampliar(url: string, nome: string, anotacoes?: Anotacao[]): void {
    const marcas = anotacoes ?? [];
    const lupa = document.createElement("div");
    lupa.className = "ch-lupa";
    lupa.innerHTML =
      `<button class="painel-fechar ch-lupa-fechar" type="button" aria-label="Fechar imagem">${ICON_X}</button>` +
      `<div class="imgwrap">` +
      `<img src="${url}" alt="${escapar(nome)}" />` +
      (marcas.length > 0
        ? `<svg class="layersvg" viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true"></svg>` +
          `<div class="layer"></div>`
        : "") +
      `</div>`;
    // Clique no fundo também fecha — é o gesto que todo visualizador de imagem tem. Os
    // pinos param a propagação (ver `ligarBaloesDosPinos`), senão tocar num deles fecharia.
    lupa.addEventListener("click", () => lupa.remove());
    this.container.querySelector(".overlay")?.appendChild(lupa);

    if (marcas.length === 0) return;
    const wrap = lupa.querySelector<HTMLDivElement>(".imgwrap")!;
    this.pintarCamadas(wrap, marcas, marcas);
    this.ligarBaloesDosPinos(wrap, marcas);
  }

  /** Revoga os objectURLs dos anexos. Chamado ao sair da tela e no `fechar`/`destruir`. */
  private liberarAnexos(): void {
    for (const u of this.urlsAnexos) {
      try {
        URL.revokeObjectURL(u);
      } catch {
        /* ok */
      }
    }
    this.urlsAnexos = [];
  }

  /** Tela de sucesso persistente: confirma o envio, mostra o código do card
      (protocolo) e — quando o intake devolveu a `portalUrl` — oferece o link para
      acompanhar os chamados. Fechar volta ao estado de repouso.

      Aqui também é onde o chamado entra no storage: o `chamadoToken` do 202 só existe
      neste instante (o servidor guarda apenas o hash), então guardá-lo é o que faz o
      chamado aparecer em "Meus chamados" — e é a diferença entre a pessoa sair com um
      código na mão e sair com um lugar para acompanhá-lo. */
  private renderSucesso(codigo?: string, portalUrl?: string, chamadoToken?: string, titulo?: string): void {
    // Guarda a origem REAL do front: o popup de login precisa dela quando o bundle é
    // servido de outro host (CDN) e a origem do intake não serve.
    if (portalUrl) this.portalUrl = portalUrl;
    this.liberarUrl();
    this.anotacoes = [];
    this.preview = null;
    this.screenshot = null;
    this.anexoDoReport = null; // já subiu; deixá-lo aqui o mandaria de novo no próximo report

    // Guarda o chamado ANTES de montar a tela: se o storage estiver indisponível a
    // gravação falha em silêncio (o storage engole), mas nunca deixa a tela sem pintar.
    const guardado = codigo !== undefined && chamadoToken !== undefined;
    if (guardado) {
      this.locais = registrarChamado(this.cfg.token, {
        codigo,
        token: chamadoToken,
        titulo: titulo?.trim() || codigo,
      });
      // Primeiro chamado deste browser: o poller não subiu no construtor (não havia o que
      // acompanhar). Sem isto, quem acabou de reportar só veria a badge no próximo load.
      this.ligarPoller();
    }

    const protocolo = codigo
      ? `<p class="okcode">Protocolo: <strong>${escapar(codigo)}</strong></p>
         <p class="okhint">${
           guardado
             ? "Você pode acompanhar as respostas em <strong>Meus chamados</strong>, aqui mesmo."
             : "Guarde este código para acompanhar o report."
         }</p>`
      : `<p class="okhint">Recebemos seu report. Obrigado!</p>`;
    // O link para o portal continua ofertado: é o caminho de quem trocar de aparelho ou
    // limpar o storage — o token deste chamado vive só neste browser.
    const link = linkDoPortal(portalUrl, this.cfg.reporter?.email);
    const acompanhar = link
      ? `<p class="okhint"><a class="oklink" href="${escapar(link)}" target="_blank" rel="noopener noreferrer">Acompanhar no portal →</a></p>`
      : "";
    this.container.innerHTML = `
      <div class="overlay">
        <div class="review success">
          <div class="okicon">✓</div>
          <h2>Report enviado!</h2>
          ${protocolo}
          ${acompanhar}
          <div class="actions center">
            ${guardado ? `<button class="btn" type="button" data-act="chamados">Meus chamados</button>` : ""}
            <button class="btn primary" type="button" data-act="ok">Fechar</button>
          </div>
        </div>
      </div>`;
    this.container.querySelector<HTMLButtonElement>('[data-act="ok"]')!.addEventListener("click", () => this.fechar());
    this.container
      .querySelector<HTMLButtonElement>('[data-act="chamados"]')
      ?.addEventListener("click", () => this.abrirPainel());
  }

  /** O e-mail utilizável que a página HOST afirmou no init — ver `campoEmail.ts`. */
  private emailDoHost(): string {
    return emailDoHostUtilizavel(this.cfg.reporter?.email);
  }

  /** O campo (ou a linha) de identificação. A regra e o porquê vivem em `campoEmail.ts`. */
  private htmlDoCampoEmail(): string {
    return htmlDoCampoEmail({
      reporterEmail: this.cfg.reporter?.email,
      emailLembrado: lerReporterEmail(this.cfg.token),
      obrigatorio: !this.cfg.aceitaAnonimo,
      // Quem entrou não digita e-mail: quem o carimba no chamado é o servidor, a partir da
      // sessão. O que a tela mostra é o RECIBO do login — a pessoa acabou de entrar numa
      // janela de um domínio que não é o do site, e sem essa linha não haveria sinal
      // nenhum de que o login pegou.
      identidade: this.identificadoPorLogin() ? this.sessao?.email ?? this.sessao?.nome ?? "" : undefined,
    });
  }

  /** Tela única: print anotável (esquerda) + formulário (direita). */
  private renderReport(rascunho?: { titulo: string; descricao: string; email: string }): void {
    this.preview = null;
    const comPrint = this.screenshotUrl !== null;
    // O painel existe quando há print E quando ela está a caminho. Sem nenhum dos dois ele
    // não é renderizado: uma moldura vazia com a instrução "clique = ponto · arraste = caixa"
    // ao lado seriam três afirmações falsas de uma vez.
    const comPainel = comPrint || this.gerandoPrint;
    // A caixa já nasce com a PROPORÇÃO da tela que está sendo redesenhada, para a imagem
    // entrar no lugar exato do aviso — sem o pulo de layout que jogaria o formulário para
    // baixo no meio da digitação.
    const proporcao = `${Math.max(1, window.innerWidth)} / ${Math.max(1, window.innerHeight)}`;
    const aviso = comPrint
      ? ""
      : `<div class="gerando" style="aspect-ratio:${proporcao}">
            <span class="gerando-barra"></span>
            <span class="gerando-txt">Gerando a imagem desta tela…</span>
            <span class="gerando-sub">Pode ir escrevendo — ela entra aqui quando ficar pronta.</span>
          </div>`;
    const paneShot = comPainel
      ? `<div class="pane-shot">
            <div class="imgwrap${comPrint ? " crosshair" : ""}">
              <img class="shot" alt="Captura da tela"${comPrint ? "" : ' hidden'} />
              <svg class="layersvg" viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true"></svg>
              <div class="layer"></div>
              <div class="pin-tip" role="tooltip" hidden></div>
              ${aviso}
            </div>
          </div>`
      : "";

    this.container.innerHTML = `
      <div class="overlay">
        <div class="content${comPainel ? "" : " sem-shot"}">
          ${paneShot}
          <div class="pane-form">
            <h2>${escapar(this.tituloDoFormulario())}</h2>
            ${this.htmlDoSeletorDeTipo()}
            <div class="field">
              <label class="label" for="altbug-titulo">Título</label>
              <input class="input" id="altbug-titulo" placeholder="Resuma o problema" maxlength="80" />
            </div>
            <div class="field">
              <label class="label" for="altbug-desc">Descrição</label>
              <textarea class="textarea" id="altbug-desc" placeholder="O que aconteceu? O que você esperava?" maxlength="500"></textarea>
            </div>
            ${this.htmlDoCampoEmail()}
            <div class="field marks-field" style="display:none">
              <span class="label">Comentários das marcas</span>
              <div class="pins"></div>
            </div>
            ${comPrint ? "" : this.htmlDosBotoesDePrint()}
            <!-- Anexo de QUALQUER tipo (DEV-211): log, planilha, contrato, o pdf que
                 explica o caso. Fica ao lado do "anexar captura" porque as duas são a
                 mesma pergunta ("o que mais ajuda a entender?"), e o erro tem âncora
                 própria — o servidor recusa por tipo/tamanho e a frase é dele. -->
            <button class="btn anexo-add" type="button" data-act="anexar-arquivo">${ICON_CLIPE}<span>Anexar um arquivo</span></button>
            <div class="anexo-escolhido" hidden></div>
            <p class="erro-form" data-erro="anexo" hidden></p>
            <p class="hint" data-hint="form">${escapar(this.dicaDoFormulario())}</p>
            <p class="erro-form" data-erro="form" hidden></p>
            <div class="actions">
              <button class="btn" type="button" data-act="cancel">Cancelar</button>
              <button class="btn primary" type="button" data-act="send">Enviar</button>
            </div>
          </div>
        </div>
      </div>`;

    const img = this.container.querySelector<HTMLImageElement>(".shot");
    if (img && this.screenshotUrl) {
      img.src = this.screenshotUrl;
      img.addEventListener("load", () => this.desenharMarcas(true), { once: true });
    }

    this.container.querySelector<HTMLButtonElement>('[data-act="cancel"]')!.addEventListener("click", () => this.fechar());
    this.container
      .querySelector<HTMLButtonElement>('[data-act="capturar"]')
      ?.addEventListener("click", () => void this.capturarNoFormulario());
    this.container
      .querySelector<HTMLButtonElement>('[data-act="print-alternativo"]')
      ?.addEventListener("click", () => void this.escapeDePrint());
    this.container
      .querySelector<HTMLButtonElement>('[data-act="anexar-arquivo"]')
      ?.addEventListener("click", () => void this.escolherAnexoDoReport());
    // O chip é pintado a partir do campo, não do DOM anterior: esta tela é reconstruída ao
    // capturar a print, e o arquivo escolhido tem de sobreviver a isso (como o rascunho).
    this.pintarAnexoDoReport();

    const titulo = this.container.querySelector<HTMLInputElement>("#altbug-titulo")!;
    const desc = this.container.querySelector<HTMLTextAreaElement>("#altbug-desc")!;
    // O input SÓ existe quando o host não informou e-mail (ver `htmlDoCampoEmail`), então
    // aqui ele é opcional — com host, o valor vem do próprio `cfg.reporter`.
    const email = this.container.querySelector<HTMLInputElement>("#altbug-email");
    // Rascunho preservado entre re-renders (capturar a print reconstrói a tela).
    if (rascunho) {
      titulo.value = rascunho.titulo;
      desc.value = rascunho.descricao;
      if (email) email.value = rascunho.email;
    }
    // O erro sai ao começar a corrigir: mantê-lo aceso enquanto a pessoa digita faz o campo
    // parecer errado quando ela já está consertando.
    email?.addEventListener("input", () => this.erroDoCampo("email", null));
    this.container.querySelectorAll<HTMLInputElement>('[name="altbug-tipo"]').forEach((r) => {
      r.addEventListener("change", () => {
        this.tipoEscolhido = r.value;
        this.erroDoCampo("form", null);
      });
    });
    this.container.querySelector<HTMLButtonElement>('[data-act="send"]')!.addEventListener("click", () => {
      void this.enviar(titulo.value, desc.value, email?.value ?? this.emailDoHost());
    });

    this.renderPainelMarcas();
    if (comPrint) {
      this.ligarDesenho();
      this.desenharMarcas(true);
    }
    titulo.focus();
  }

  /** O título muda com o caminho: "problema" é o vocabulário do report de bug, e no
      caminho secundário ele estaria nomeando errado o que a pessoa veio fazer. */
  private tituloDoFormulario(): string {
    // Quem decide é o CAMINHO (`tipoEscolhido`), não a presença da print. O `screenshot
    // !== null` que estava aqui era inofensivo enquanto o caminho principal SEMPRE chegava
    // com print; desde o DEV-277 ele chega sem — a captura recusada abre este formulário —,
    // e a condição antiga passava a intitular "Abrir chamado" a tela de quem clicou em
    // "Reportar bug".
    return this.tipoEscolhido === null ? "Descreva o problema" : this.cfg.rotuloSecundario;
  }

  /**
   * A frase de rodapé, DERIVADA do estado real. Ela era fixa e prometia "a print, as
   * anotações e os logs do console" — no caminho secundário as três podem ser falsas: não
   * há print (a menos que a pessoa clique no botão), sem print não há anotações, e o
   * console acompanha só o tipo principal (o servidor decide isso, DEV-144).
   */
  /** Os botões de print: o primário sempre, o alternativo só depois de uma falha. */
  private htmlDosBotoesDePrint(): string {
    const botao = (act: string, rotulo: string): string =>
      `<button class="btn shot-add" type="button" data-act="${act}">${ICON_CAMERA}` +
      `<span>${escapar(rotulo)}</span></button>`;

    return (
      botao("capturar", this.rotuloDaCaptura()) +
      (this.mostraEscapeDePrint() ? botao("print-alternativo", "Anexar um print do aparelho") : "")
    );
  }

  /** O clique no caminho alternativo — o print do aparelho, que só o celular oferece. */
  private async escapeDePrint(): Promise<void> {
    await this.imagemNoFormulario(this.lerRascunho());
  }

  private dicaDoFormulario(): string {
    const partes: string[] = [];
    if (this.screenshot) partes.push("a captura da tela");
    if (this.screenshot && this.anotacoes.length) partes.push("as suas marcas");
    if (this.tipoEscolhido === null && this.cfg.consoleHabilitado) partes.push("os logs do console");

    if (partes.length === 0) return "Ao enviar, um chamado será aberto e o time responde por aqui.";
    // `.at(-1)` não existe no target es2020 do bundle — índice explícito.
    const lista =
      partes.length > 1 ? `${partes.slice(0, -1).join(", ")} e ${partes[partes.length - 1]}` : partes[0];

    return `Ao enviar, um chamado será aberto com ${lista}.`;
  }

  /**
   * O seletor de tipo: só com DOIS ou mais tipos adicionais. Com um só, o item do menu já
   * determinou o tipo, e um seletor de uma opção é UI morta.
   *
   * Radio, e não `<select>`: são poucas opções e todas cabem na tela — ver o conjunto é o
   * que deixa a pessoa perceber que existe uma escolha, o que um select fechado esconde.
   */
  private htmlDoSeletorDeTipo(): string {
    const tipos = this.cfg.tiposAdicionais;
    if (this.tipoEscolhido === null || tipos.length < 2) return "";

    const opcoes = tipos
      .map(
        (t) =>
          `<label class="tipo-op"><input type="radio" name="altbug-tipo" value="${escapar(t.id)}"` +
          `${t.id === this.tipoEscolhido ? " checked" : ""} /><span>${escapar(t.label)}</span></label>`,
      )
      .join("");

    return `<div class="field"><span class="label">Sobre o que é?</span><div class="tipo-ops">${opcoes}</div></div>`;
  }

  /** (Re)constrói só a lista de comentários das marcas (painel direito). Chamada
      ao criar/desfazer uma marca; mantém o binding input → anotacoes[i].comentario. */
  private renderPainelMarcas(): void {
    const field = this.container.querySelector<HTMLDivElement>(".marks-field");
    const pins = this.container.querySelector<HTMLDivElement>(".pins");
    if (!field || !pins) return;
    field.style.display = this.anotacoes.length ? "" : "none";
    pins.innerHTML = this.anotacoes
      .map(
        (_a, i) =>
          `<div class="pinrow"><span class="pinnum">${i + 1}</span><input class="input" data-i="${i}" placeholder="Comentário da marca ${i + 1}" /><button class="btn iconbtn" type="button" data-del="${i}" aria-label="Remover marca ${i + 1}" title="Remover marca">✕</button></div>`,
      )
      .join("");
    pins.querySelectorAll<HTMLInputElement>("input[data-i]").forEach((inp) => {
      const idx = Number(inp.dataset.i);
      inp.value = this.anotacoes[idx]?.comentario ?? "";
      // Só guarda o texto. NÃO redesenha: o `desenharMarcas(true)` que morava aqui existia
      // apenas para o pino trocar de cor ao ganhar comentário, e o pino não muda mais de cor.
      // De brinde, para de repintar as duas camadas e re-registrar os listeners do tooltip a
      // cada tecla digitada.
      inp.addEventListener("input", () => {
        const a = this.anotacoes[idx];
        if (a) a.comentario = inp.value;
      });
    });
    pins.querySelectorAll<HTMLButtonElement>("button[data-del]").forEach((btn) => {
      btn.addEventListener("click", () => this.removerMarca(Number(btn.dataset.del)));
    });
  }

  /** Remove a marca de índice `i` (botão ✕ ao lado do comentário) e re-renderiza
      a lista + a print. Os números seguintes reindexam sozinhos. */
  private removerMarca(i: number): void {
    if (i < 0 || i >= this.anotacoes.length) return;
    this.anotacoes.splice(i, 1);
    this.renderPainelMarcas();
    this.desenharMarcas(true);
  }

  /* ---------------- Interações ---------------- */

  private ligarDesenho(): void {
    const wrap = this.container.querySelector<HTMLDivElement>(".imgwrap")!;
    let down: { cx: number; cy: number; p: { x: number; y: number } } | null = null;
    let arrastou = false;
    const rect = () => wrap.getBoundingClientRect();

    wrap.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      down = { cx: e.clientX, cy: e.clientY, p: normalizarPonto(e.clientX, e.clientY, rect()) };
      arrastou = false;
      wrap.setPointerCapture(e.pointerId);
    });

    wrap.addEventListener("pointermove", (e) => {
      if (!down) return;
      const dist = Math.hypot(e.clientX - down.cx, e.clientY - down.cy);
      if (dist < LIMIAR_ARRASTE) return; // ainda é clique
      arrastou = true;
      const p = normalizarPonto(e.clientX, e.clientY, rect());
      this.preview = { tipo: "rect", x: down.p.x, y: down.p.y, x2: p.x, y2: p.y };
      this.desenharMarcas(true);
    });

    const finalizar = (e: PointerEvent) => {
      if (!down) return;
      const p = normalizarPonto(e.clientX, e.clientY, rect());
      const d = down;
      down = null;
      this.preview = null;

      if (!arrastou) {
        this.anotacoes.push({ tipo: "pin", x: d.p.x, y: d.p.y }); // clique = ponto
      } else {
        const caixa: Anotacao = { tipo: "rect", x: d.p.x, y: d.p.y, x2: p.x, y2: p.y };
        if (!anotacaoValida(caixa)) {
          this.desenharMarcas(true);
          return;
        }
        this.anotacoes.push(caixa); // arraste = caixa
      }
      const idx = this.anotacoes.length - 1;
      this.renderPainelMarcas();
      this.desenharMarcas(true);
      this.container.querySelector<HTMLInputElement>(`.pins input[data-i="${idx}"]`)?.focus();
    };
    wrap.addEventListener("pointerup", finalizar);
    wrap.addEventListener("pointercancel", () => {
      down = null;
      this.preview = null;
      this.desenharMarcas(true);
    });
  }

  /** Renderiza caixas (SVG) + marcadores numerados. `interativo` liga o clique
      no pino para focar o comentário correspondente no painel direito. */
  private desenharMarcas(interativo: boolean): void {
    const wrap = this.container.querySelector<HTMLDivElement>(".imgwrap");
    if (!wrap) return;

    const shapes = this.preview ? [...this.anotacoes, this.preview] : this.anotacoes;
    this.pintarCamadas(wrap, shapes, this.anotacoes);
    if (!interativo) return;

    const layer = wrap.querySelector<HTMLDivElement>(".layer");
    layer?.querySelectorAll<HTMLElement>(".pin").forEach((el) => {
      el.addEventListener("pointerdown", (e) => {
        e.stopPropagation();
        e.preventDefault();
        const i = Number(el.dataset.i);
        this.container.querySelector<HTMLInputElement>(`.pins input[data-i="${i}"]`)?.focus();
      });
    });
    this.ligarTooltipDosPinos(wrap, this.anotacoes);
  }

  /**
   * Põe as duas camadas de marca sobre um wrap. `formas` pode incluir a prévia do arraste;
   * `pinos` são só as marcas de verdade (a prévia não ganha número).
   */
  private pintarCamadas(wrap: HTMLElement, formas: readonly Anotacao[], pinos: readonly Anotacao[]): void {
    const svg = wrap.querySelector<SVGSVGElement>(".layersvg");
    const layer = wrap.querySelector<HTMLDivElement>(".layer");
    if (!svg || !layer) return;
    svg.innerHTML = svgDasMarcas(formas);
    layer.innerHTML = pinosDasMarcas(pinos);
  }

  /**
   * Os comentários VISÍVEIS sobre a print, e o clique no pino escondendo/mostrando o dele.
   *
   * É a leitura, não a edição: aqui a pessoa está abrindo um report que já existe, e o que
   * ela quer é ler o que foi escrito. Enquanto o comentário só aparecia no hover, ele
   * precisava ser DESCOBERTO um a um — e no celular, onde esta tela mais roda, o hover não
   * existe (o `pointerleave` chega quando o dedo levanta). Então todos nascem abertos, e o
   * clique no pino é o que tira da frente o que atrapalha: marcas próximas se sobrepõem, e
   * esconder um balão é mais barato do que arrastar a imagem.
   *
   * O toggle é POR MARCA e mora no `hidden` do próprio balão — sem estado no widget, então
   * reabrir a lupa recomeça com tudo visível, que é o estado que a tela promete.
   *
   * O texto vai por `textContent`, nunca por `innerHTML`: ele vem de um POST anônimo que o
   * intake não valida elemento a elemento, e esta é a página do cliente.
   *
   * (O EDITOR — a tela de captura — segue no hover, com `ligarTooltipDosPinos`: lá o
   * comentário está sendo digitado na lista ao lado, e repeti-lo aberto sobre a imagem
   * cobriria justamente o que se está anotando.)
   */
  private ligarBaloesDosPinos(wrap: HTMLElement, marcas: readonly Anotacao[]): void {
    const layer = wrap.querySelector<HTMLDivElement>(".layer");
    if (!layer) return;

    layer.insertAdjacentHTML("beforeend", baloesDasMarcas(marcas));
    const baloes = new Map<string, HTMLElement>();
    for (const balao of layer.querySelectorAll<HTMLElement>(".pin-tip")) {
      const i = balao.dataset.i ?? "";
      balao.textContent = marcas[Number(i)]?.comentario?.trim() ?? "";
      baloes.set(i, balao);
    }

    for (const pino of layer.querySelectorAll<HTMLElement>(".pin")) {
      // `stopPropagation` porque a lupa fecha ao clique no fundo: sem isto, tocar num pino
      // fecharia a imagem em vez de esconder o comentário.
      pino.addEventListener("click", (e) => {
        e.stopPropagation();
        const balao = baloes.get(pino.dataset.i ?? "");
        if (balao) balao.hidden = !balao.hidden;
      });
    }
  }

  /**
   * Tooltip do comentário no pino do EDITOR: hover passa, CLIQUE fixa.
   *
   * Os dois estados existem por causa do TOQUE, e não por gosto. A tela de acompanhamento é
   * a que roda em celular (a captura é desktop-only por `getDisplayMedia`; ler o chamado
   * não), e ali só hover não serve: no toque o `pointerleave` dispara quando o dedo LEVANTA,
   * então um tooltip só-hover aparece e desaparece no mesmo gesto. Com o clique fixando —
   * ele chega depois do `pointerleave` —, o comentário fica na tela até tocar de novo.
   *
   * Enquanto nada está fixado, o mouse manda: entra mostra, sai esconde.
   *
   * O texto vai por `textContent`, nunca por `innerHTML`: ele vem de um POST anônimo que o
   * intake não valida elemento a elemento, e esta é a página do cliente.
   */
  private ligarTooltipDosPinos(wrap: HTMLElement, marcas: readonly Anotacao[]): void {
    const tip = wrap.querySelector<HTMLDivElement>(".pin-tip");
    if (!tip) return;
    let fixado: HTMLElement | null = null;

    const mostrar = (el: HTMLElement): void => {
      const marca = marcas[Number(el.dataset.i)];
      const c = marca?.comentario?.trim();
      tip.textContent = c || "Sem comentário";
      tip.classList.toggle("vazio", !c);
      tip.style.left = el.style.left;
      tip.style.top = el.style.top;
      // Marca no alto da print: o tooltip desce, senão o recorte do wrap o corta.
      tip.classList.toggle("abaixo", marca !== undefined && pontoDaMarca(marca)[1] < 0.12);
      tip.hidden = false;
    };

    wrap.querySelectorAll<HTMLElement>(".pin").forEach((el) => {
      el.addEventListener("pointerenter", () => {
        if (fixado === null) mostrar(el);
      });
      el.addEventListener("pointerleave", () => {
        if (fixado === null) tip.hidden = true;
      });
      // `stopPropagation` porque a lupa fecha ao clique no fundo: sem isto, tocar num pino
      // fecharia a imagem em vez de mostrar o comentário.
      el.addEventListener("click", (e) => {
        e.stopPropagation();
        if (fixado === el) {
          fixado = null;
          tip.hidden = true;

          return;
        }
        fixado = el;
        mostrar(el);
      });
    });
  }

  /* ---------------- Utils ---------------- */

  private setBotaoEnviarEstado(desabilitado: boolean): void {
    const b = this.container.querySelector<HTMLButtonElement>('[data-act="send"]');
    if (b) {
      b.disabled = desabilitado;
      b.textContent = desabilitado ? "Enviando…" : "Enviar";
    }
  }

  /** Vai no CONTAINER, não no shadow root: custom property declarada em `.altbug` não sobe
      para um irmão, então o toast solto na raiz não enxergava `--ab-inverso` (e por isso a
      cor dele era literal, igual ao painel do tema escuro — invisível). Dentro do container
      ele herda os tokens; a posição não muda, porque é `position: fixed` e nada no caminho
      cria contexto de empilhamento ou bloco contentor. */
  private toast(msg: string): void {
    const t = document.createElement("div");
    t.className = "toast";
    t.textContent = msg;
    this.container.appendChild(t);
    setTimeout(() => t.remove(), 3200);
  }

  private liberarUrl(): void {
    if (this.screenshotUrl) {
      URL.revokeObjectURL(this.screenshotUrl);
      this.screenshotUrl = null;
    }
    // Os anexos da conversa seguem a mesma vida: fechar o fluxo os solta.
    this.liberarAnexos();
  }
}

/**
 * A consulta que separa celular de desktop (DEV-224): `(hover: none)`, a MESMA do CSS.
 *
 * Não é "é mobile?" nem largura de tela — um tablet ligado a um mouse tem hover e merece a
 * bolinha, e uma janela estreita no desktop continua sendo desktop. O que decide é o gesto
 * disponível: sem hover, o menu não tem como abrir sozinho, e é isso que o talo resolve.
 *
 * Devolve `null` (= desktop) onde `matchMedia` não existe. Ele existe em todo browser que
 * roda o widget; a guarda é para o jsdom dos testes e para host exótico não derrubar o
 * construtor inteiro.
 */
function consultarToque(): MediaQueryList | null {
  try {
    return window.matchMedia?.("(hover: none)") ?? null;
  } catch {
    return null;
  }
}

/** Tamanho legível para o chip de arquivo. KB basta: o teto do intake é 20 MB. */
function tamanhoCurto(bytes: number): string {
  if (bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}


/** Escurece um hex ("#rgb"|"#rrggbb") por `fator` (0..1) — usado no acento :hover. */
function escurecer(hex: string, fator: number): string {
  let h = hex.replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-f]{6}$/i.test(h)) return hex;
  const n = Number.parseInt(h, 16);
  const k = Math.max(0, Math.min(1, 1 - fator));
  const r = Math.round(((n >> 16) & 0xff) * k);
  const g = Math.round(((n >> 8) & 0xff) * k);
  const b = Math.round((n & 0xff) * k);
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}
